import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import {
    resetFlow,
    addNode,
    setActiveNode,
    setRecommendations,
    setLoading,
    setError,
    addToSetPath,
    removeRecommendationNodes,
    updateNodePosition,
    setPan,
    setZoom,
    markNodeSelected,
    undoLastTrack,
    breakChainAt,
    updateNodeTrack,
    clearPendingStart,
    loadSavedState,
} from '../../redux/slices/whatsNextSlice';
import { searchTrack, searchArtist, ensureToken } from '../../redux/thunk/djpalThunk';
import useDjpalConnect from '../../hooks/useDjpalConnect';
import { setActiveSearch, setTrackQuery, setArtistQuery } from '../../redux/slices/djpalSlice';
import { setActiveTab, pushNavEntry } from '../../redux/slices/appSlice';
import { navigateHistory } from '../../redux/thunk/appNavThunk';
import { selectHistoryByType, selectSurfaceLoaded } from '../../redux/slices/searchHistorySlice';
import { addSearchHistoryEntry, deleteSearchHistoryEntry, clearSearchHistoryByType, fetchSearchHistory } from '../../redux/thunk/searchHistoryThunk';
import { addTrack, resetAudioList, setAutoPlay, setAutoRadio } from '../../redux/slices/musicPlayerSlice';
import { addToNextUp, fullFirstQueueSrc } from '../../utils/queueAdd';
import { resolveCover } from '../../utils/cover';
import { resolveTrackSrc } from '../../utils/resolvePlaybackSrc';
import { playAllTracks } from '../../utils/playAllTracks';
import { start as startPlayTiming } from '../../playback/playTiming';
import { clearListContext } from '../../redux/slices/listContextSlice';
import { likeTrack as likeTrackThunk, unlikeTrack as unlikeTrackThunk } from '../../redux/thunk/likesThunk';
import { setPlaylists, setSelectedPlaylist, triggerPlaylistRefresh } from '../../redux/slices/playlistSlice';
import { useToast } from '../../contexts/ToastContext';
import { useTierGate } from '../../hooks/useTierGate';
import { GoatBadge } from '../Tier';
import { isWeb as _isWeb, isNative as _isNative, notifyWebOnly } from '../../utils/webOnly';
import { MIN_SEARCH_CHARS } from '../../utils/searchConfig';
import { entityId } from '../../utils/entityId';
import { safeArtwork } from '../../utils/no1001';
import { track as trackEvent } from '../../utils/analytics';
import { refreshAndRetry } from '../../redux/utils/refreshAndRetry';
import { FREE_STEP_LIMIT, FREE_SET_LIMIT, canCreateNewSet, localPlannerQuota } from '../../utils/plannerLimits';
import { keyCompatScore, bpmDiffRatio, formatKeyDisplay, camelotColor } from '../../utils/harmonic';
import AddToPlaylistModal from '../AddToPlaylistModal';
import DJPalSearchBar from '../DJPal/DJPalSearchBar';
import { ReactComponent as PlayIcon } from '../../assets/icons/play.svg';
import { ReactComponent as PauseIcon } from '../../assets/icons/pause.svg';
import '../WhatsNext/WhatsNext.scss';
import { errMsg } from '../../redux/utils/errMsg';
import { estimateSetSeconds, estimateSavedSetSeconds, formatSetLength } from '../../utils/setLength';
import { useDesignV2 } from '../../utils/designV2';
import { generateShareImage, generateShareImagePages, shareImages } from '../../utils/generateShareImage';
import { userRealName } from '../../utils/userDisplay';
import { updateSettings } from '../../redux/slices/settingsSlice';
import { SETS_MIGRATED_KEY, loadSavedSets, persistSavedSets, loadKnownSetCount, rememberSetCount } from '../../utils/plannerSetsCache';

const NODE_W = 84;
const NODE_H = 140;
const LEVEL_GAP_X = 220;
const CHILD_GAP_Y = 150;
const START_X = 80;

const RECENT_LIMIT = 8;

// Mobile-only: detected once per render via matchMedia. Every call site that
// branches on this is read-only in the mobile path; desktop behavior is
// untouched.
const checkIsMobileWeb = () =>
    _isWeb && typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;

let nodeIdCounter = 0;
const genId = () => `wn-${++nodeIdCounter}`;

const getPreviewUrl = (track) =>
    track?.previewUrl || track?.sampleUrl || track?.preview_url || track?.preview || null;

// Local library files aren't playable in <audio> directly — wrap them through
// the Electron audio server. Cloud placeholders (online://) have no source.
const filePathToAudioUrl = (filePath) => {
    if (!filePath) return null;
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) return filePath;
    if (filePath.startsWith('online://')) return null;
    return `http://localhost:3069/audio?path=${encodeURIComponent(filePath)}`;
};

// Album art for a local file (the server returns a default cover on miss).
const filePathToArtUrl = (filePath) => {
    if (!filePath || filePath.startsWith('online://')) return null;
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) return filePath;
    return `http://localhost:3069/album-art?path=${encodeURIComponent(filePath)}`;
};

// Strip a trailing audio extension so a filename-derived title ("Fuckin
// Move.wav") doesn't leak into Mixpedia searches / display.
const stripAudioExt = (s) =>
    (s || '').replace(/\.(wav|mp3|flac|aiff?|m4a|aac|ogg|wma|alac)$/i, '').trim();

// Library tracks may be saved with empty title (no ID3 enrichment yet) or
// with a title that mirrors the "Artist - Title" file-name shape (the artist
// metadata sometimes only carries the primary artist while title carries the
// full multi-artist string + dash + title). Strip when the prefix mentions
// the artist; otherwise leave the title alone.
const stripArtistPrefix = (title, artist) => {
    if (!title || !artist) return title || '';
    const dashIdx = title.indexOf(' - ');
    if (dashIdx <= 0) return title;
    const prefix = title.slice(0, dashIdx);
    if (prefix.toLowerCase().includes(artist.toLowerCase())) {
        return title.slice(dashIdx + 3).trim();
    }
    return title;
};

const getTrackTitle = (t) => {
    const artist = (t?.track?.artist || t?.artist || '').trim();
    let title = (t?.track?.title || t?.track?.trackName || t?.title || '').trim();

    // Bad metadata: title literally repeats the artist with no real title
    // attached. Treat it as missing and fall through to the filename.
    if (artist && title.toLowerCase() === artist.toLowerCase()) {
        title = '';
    }

    if (!title) {
        const fileName = t?.fileName || t?.file_name;
        if (fileName) {
            title = fileName.replace(/\.(mp3|wav|aif|aiff|flac|m4a|aac|ogg|wma|alac)$/i, '').trim();
        }
    }
    return stripArtistPrefix(title, artist);
};

const getArtwork = (track) =>
    safeArtwork(track?.artwork || track?.artworkUrl || track?.image || null);

// Wrap every occurrence of the search query in <mark> so rows visually
// confirm what matched. Split on a capture group puts the hits at odd indices.
const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const highlightMatch = (text, query) => {
    const t = text || '';
    const q = (query || '').trim();
    if (!q) return t;
    const parts = t.split(new RegExp(`(${escapeRegExp(q)})`, 'ig'));
    if (parts.length === 1) return t;
    return parts.map((part, i) =>
        i % 2 === 1 ? <mark className="saved-set-hl" key={i}>{part}</mark> : part
    );
};

// Saved-set date: "today"/"yesterday" for instant recency, otherwise a short
// locale-aware date with the month spelled out ("Jul 23", + year when not
// this year) — month names sidestep the US-vs-EU day/month ambiguity.
const formatSetDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const days = Math.round((startOfDay(now) - startOfDay(d)) / 86400000);
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    const opts = { month: 'short', day: 'numeric' };
    if (d.getFullYear() !== now.getFullYear()) opts.year = 'numeric';
    return d.toLocaleDateString(undefined, opts);
};

// Estimated set length chip: "~52m" — the tilde alone carries "estimate"
// (tried clock+tilde, clock+"est.", "≈": all read as busy); the desktop
// tooltip has the full wording.
const SET_LENGTH_TITLE = 'Estimated set length: track durations minus ~1 minute per transition';
const SetLength = ({ seconds, className = '' }) => {
    const label = formatSetLength(seconds);
    if (!label) return null;
    return (
        <span className={`set-length${className ? ` ${className}` : ''}`} title={SET_LENGTH_TITLE}>
            {label}
        </span>
    );
};

// Cover shown for a saved set: the user's custom cover wins; otherwise fall
// back to the first set track that has artwork (local files go through the
// album-art server). Works on anything shaped like { cover, nodes, setPath }.
const getSetCoverArt = (set) => {
    if (set?.cover) return set.cover;
    for (const id of set?.setPath || []) {
        const t = set.nodes?.[id]?.track;
        const art = t && (getArtwork(t) || filePathToArtUrl(t.filePath));
        if (art) return art;
    }
    return null;
};

// Start-screen tile art: the custom cover if set; otherwise the set's
// DISTINCT track covers (up to 4), so sets that start from the same track
// don't all wear the same face. The tile splits by count — 4 → 2×2,
// 3 → one tall left + two stacked right, 2 → side by side, 1 → single
// (CSS: .start-recent-cover--n2/--n3/--n4). No repeated cells.
const getSetCoverMosaic = (set) => {
    if (set?.cover) return [set.cover];
    const arts = [];
    for (const id of set?.setPath || []) {
        const t = set.nodes?.[id]?.track;
        const art = t && (getArtwork(t) || filePathToArtUrl(t.filePath));
        if (art && !arts.includes(art)) arts.push(art);
        if (arts.length === 4) break;
    }
    return arts;
};

// Downscale an uploaded cover to a compact JPEG data URL. Set snapshots live
// in a 1MB JSONB column server-side, so a raw phone photo data URL would blow
// the cap — 512px JPEG keeps covers well under it.
const downscaleCoverImage = (dataUrl) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
        try {
            const max = 512;
            const scale = Math.min(1, max / Math.max(img.width, img.height));
            const w = Math.max(1, Math.round(img.width * scale));
            const h = Math.max(1, Math.round(img.height * scale));
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            // JPEG has no alpha — flatten transparent PNGs onto white instead
            // of letting them go black.
            ctx.fillStyle = '#fff';
            ctx.fillRect(0, 0, w, h);
            ctx.drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL('image/jpeg', 0.85));
        } catch {
            resolve(dataUrl);
        }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
});

// Persistence helpers live in utils/plannerSetsCache.js (shared with the
// logout thunk, which wipes the cache so it never leaks across accounts).

// Signature of the meaningful set state — used to detect unsaved changes.
// Only the ordered list of set tracks counts. Deliberately EXCLUDED, since
// none of them are user edits worth a "Save changes?" nag on Back/Reset:
//   - pan/zoom and node x/y (viewport / layout; load recomputes a fit anyway)
//   - recommendation nodes (fetched async — a rec batch that lands after ⌘S
//     used to flip a just-saved set back to dirty)
//   - track metadata enrichment on existing nodes (artwork/id/links filled in
//     by the recs response or the catalog hydrate after load)
// setPath holds node ids, and a node id is never reassigned to a different
// track (edits add/remove nodes), so the path alone identifies the content.
const makeSetSignature = (nodes, nodeOrder, setPath) =>
    JSON.stringify(setPath || []);

// Stable id for a saved set so re-saving updates the same entry instead of
// creating a new one. UUIDs so the same set can be upserted from any device;
// legacy saves have no id and are keyed by name until re-saved.
let savedSetIdCounter = 0;
const genSetId = () =>
    (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `set-${Date.now().toString(36)}-${(savedSetIdCounter++).toString(36)}`;

// Server row <-> client set shape. The server stores { id, name, data } where
// data is the opaque snapshot (nodes/nodeOrder/setPath/activeNodeId; legacy
// rows may also carry pan/zoom, ignored on load in favor of a computed fit).
const serverRowToSet = (row) => ({
    id: row.id,
    name: row.name,
    date: row.updated_at || row.created_at || null,
    ...(row.data || {}),
});
const setToServerPayload = (set) => {
    const { id, name, date, pendingSync, ...data } = set;
    return { name, data };
};

const WhatsNextTab = () => {
    const dispatch = useDispatch();
    const store = useStore();
    const toast = useToast();
    const isWeb = _isWeb;
    const { canAccess, requireUpgrade } = useTierGate('whats-next');

    // On web (desktop-size only), show CTA hint about desktop library integration.
    // Skip on mobile — the Set Planner toolbar is already cramped and a floating
    // banner adds noise.
    useEffect(() => {
        if (isWeb && !checkIsMobileWeb()) {
            setTimeout(() => {
                notifyWebOnly('set-planner', 'hint');
            }, 500);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    // Free plan: FREE_STEP_LIMIT recommendation steps per set (counted in the
    // MAIN process — plannerFreeQuota) and FREE_SET_LIMIT saved sets (main +
    // user-library-service). The UI below only mirrors those decisions; none
    // of it is load-bearing against DevTools.
    const [freeQuota, setFreeQuota] = useState(null); // { used, limit } after each step (free users only)
    const [lockedPreview, setLockedPreview] = useState(null); // { moreCount } once the set's free steps are used
    const planIdRef = useRef(null); // identity of the set being built, for the step quota
    const canvasRef = useRef(null);
    const innerRef = useRef(null);

    const { nodes, nodeOrder, activeNodeId, isLoading, error, setPath, panX, panY, zoom } =
        useSelector((state) => state.whatsNext);
    const autoPlay = useSelector((state) => state.musicPlayer.autoPlay);
    // DESIGN V2 — three-group toolbar (title / add / play-share-more) and the
    // first-render fit. Hooks live up here, above the start-screen early return.
    const v2 = useDesignV2();
    const [v2AddMenu, setV2AddMenu] = useState(null); // null | 'menu' | 'new'
    const [v2MoreMenu, setV2MoreMenu] = useState(false);
    const v2AddRef = useRef(null);
    const v2MoreRef = useRef(null);
    useEffect(() => {
        if (!v2AddMenu && !v2MoreMenu) return undefined;
        const onDown = (e) => {
            if (v2AddMenu && v2AddRef.current && !v2AddRef.current.contains(e.target)) setV2AddMenu(null);
            if (v2MoreMenu && v2MoreRef.current && !v2MoreRef.current.contains(e.target)) setV2MoreMenu(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') { setV2AddMenu(null); setV2MoreMenu(false); } };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
    }, [v2AddMenu, v2MoreMenu]);
    const audioList = useSelector((state) => state.musicPlayer.audioList);
    const likedSet = useSelector((state) => state.likes.likedSet);
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const accessToken = useSelector((state) => state.auth.accessToken);
    const authUser = useSelector((state) => state.auth.user);
    const keyNotation = useSelector((state) => state.settings.keyNotation);
    const savedDjName = useSelector((state) => state.settings.djName);

    const [searchQuery, setSearchQuery] = useState('');
    // Set Planner has its own recent-searches list (separate from Mixpedia).
    // The two surfaces have different intents (research a track vs. plan a
    // set) and sharing them confused users. Cross-device sync still works —
    // both are server-backed under the same `planner` surface.
    const trackHistory = useSelector(selectHistoryByType('track', 'planner'));
    const recentSearches = (trackHistory || []).slice(0, RECENT_LIMIT);
    const historyLoaded = useSelector(selectSurfaceLoaded('planner'));
    const [playingNodeId, setPlayingNodeId] = useState(null);
    const [loadingNodeId, setLoadingNodeId] = useState(null);
    const [recentlySelectedId, setRecentlySelectedId] = useState(null);
    const [showExport, setShowExport] = useState(false);
    const [isPanning, setIsPanning] = useState(false);
    const [panStart, setPanStartState] = useState({ x: 0, y: 0 });
    const [copied, setCopied] = useState(false);
    const hasStarted = nodeOrder.length > 0;

    // Node dragging state
    const [draggingNodeId, setDraggingNodeId] = useState(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

    // Hint dismiss
    const [hintDismissed, setHintDismissed] = useState(false);

    const [exportName, setExportName] = useState('');
    // DJ / artist name printed on the PNG. Seeded from the saved preference,
    // else the account's real name (never an email prefix); edits persist.
    const [exportDjName, setExportDjName] = useState('');
    const [exportingImage, setExportingImage] = useState(false);

    // Save/load state
    const [savedSets, setSavedSets] = useState(() => loadSavedSets());
    // True while the cloud list is in flight. Only the cold path shows it —
    // a warm start hydrates from the localStorage mirror in the initializer
    // above and paints real cards on the first frame, so it must never flash
    // a skeleton (see showRecentSkeleton).
    const [setsHydrating, setSetsHydrating] = useState(false);
    // How many sets this account had the last time this device saw its list.
    // null = never seen, which is NOT the same as zero (see plannerSetsCache).
    const [knownSetCount, setKnownSetCount] = useState(() => loadKnownSetCount(authUser?.id ?? null));
    const [mockSkeleton, setMockSkeleton] = useState(false);
    const recentGridObs = useRef(null);
    const [showSavedSets, setShowSavedSets] = useState(false);
    // DESIGN V2: "Browse all" expands the row into the full grid in place — no modal.
    const [v2AllSets, setV2AllSets] = useState(false);
    // Saved Sets modal search — matches set name, description, or any track
    // (artist/title) inside the set.
    const [savedSetsQuery, setSavedSetsQuery] = useState('');
    // One analytics ping per modal open, on the first typed character.
    const savedSetsSearchTrackedRef = useRef(false);
    // In-button spinner while Queue All resolves the first track.
    const [queueAllLoading, setQueueAllLoading] = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [saveName, setSaveName] = useState('');
    // Cover/description being edited in the save dialog (staged until Save).
    const [saveCover, setSaveCover] = useState(null);
    const [saveDescription, setSaveDescription] = useState('');
    const [saveCoverDragOver, setSaveCoverDragOver] = useState(false);
    // Non-null when the dialog edits a saved set from the Saved Sets list
    // (without loading it onto the canvas) — details update in place.
    const [editSetTarget, setEditSetTarget] = useState(null);
    // True when the dialog was opened as "Save As" (⌘⇧S) — primary action
    // forks a copy instead of renaming in place.
    const [saveAsMode, setSaveAsMode] = useState(false);
    // Dropdown under the toolbar set-name chip (Rename… / Save As…).
    const [showSetMenu, setShowSetMenu] = useState(false);
    // Identity of the set currently being edited (set on load / first save) so
    // Save updates it in place. Null = a fresh, never-saved set.
    const [currentSetId, setCurrentSetId] = useState(null);
    const [currentSetName, setCurrentSetName] = useState('');
    // Optional set details, persisted inside the set snapshot (so they ride
    // the same cloud sync as the rest of the set). Cover is a data URL; null
    // means "use the first track's artwork".
    const [currentSetCover, setCurrentSetCover] = useState(null);
    const [currentSetDescription, setCurrentSetDescription] = useState('');
    // Autosave status shown in the toolbar chip: 'saving' | 'saved' | 'offline'
    const [saveStatus, setSaveStatus] = useState(null);
    // Signature of the last saved/loaded state; compared against the live state
    // to know whether there are genuinely unsaved changes. Null = no baseline
    // yet (so any built-but-unsaved set counts as dirty).
    const savedSigRef = useRef(null);

    // Whether the current canvas has unsaved changes relative to the last
    // saved/loaded baseline. A never-saved set with any tracks counts as dirty.
    const currentSig = useMemo(
        () => makeSetSignature(nodes, nodeOrder, setPath, activeNodeId),
        [nodes, nodeOrder, setPath, activeNodeId]
    );
    // Render-time flag for the toolbar dirty dot. savedSigRef mutations don't
    // re-render on their own, but every save also sets saveStatus state, which
    // does — so this stays fresh.
    const hasUnsavedChanges = setPath.length > 0 && currentSig !== savedSigRef.current;
    const isCurrentSetDirty = useCallback(() => {
        if (setPath.length === 0) return false;
        return makeSetSignature(nodes, nodeOrder, setPath, activeNodeId) !== savedSigRef.current;
    }, [nodes, nodeOrder, setPath, activeNodeId]);

    // --- Cloud sync for saved sets (user-library-service) ---
    // localStorage is the offline cache; the cloud is the source of truth once
    // authenticated. Sets saved while offline are marked pendingSync and
    // retried on the next successful list fetch.
    const cloudSetsLoadedRef = useRef(false);

    // Account switch while this tab stays mounted (logout happens behind the
    // login modal): drop the previous user's saved-sets state and re-arm the
    // once-per-session cloud fetch so the next account loads THEIR list. The
    // logout thunk has already wiped the localStorage mirror, so re-hydrating
    // from it is safe (empty when logged out, next user's cache otherwise).
    const authUserIdRef = useRef(authUser?.id ?? null);
    useEffect(() => {
        const uid = authUser?.id ?? null;
        if (authUserIdRef.current === uid) return;
        authUserIdRef.current = uid;
        cloudSetsLoadedRef.current = false;
        setSetsHydrating(false); // the fetch effect re-arms it if the new user needs one
        setKnownSetCount(loadKnownSetCount(uid));
        setSavedSets(loadSavedSets());
    }, [authUser?.id]);

    // Only writes once the cloud list is the source of truth — the mirror's
    // length before that is a cache size, not the account's set count.
    useEffect(() => {
        const uid = authUser?.id ?? null;
        if (!uid || !cloudSetsLoadedRef.current) return;
        rememberSetCount(uid, savedSets.length);
        setKnownSetCount(savedSets.length);
    }, [savedSets, authUser?.id]);

    /**
     * Dev QA hook, sibling of `window.__mockBackupProgress` (useBackupRun) and
     * `window.__mockBackupStatus` (BackupSection): pins the Recent skeleton on
     * screen. The real cold path lasts exactly one fetch — a few hundred ms —
     * and cannot be stretched from outside, since the preload bridge is a
     * contextBridge object and its methods cannot be wrapped.
     *
     *   window.__mockRecentSkeleton()        // pin the ghost row
     *   window.__mockRecentSkeleton(false)   // back to the truth
     *
     * Never present in production builds.
     */
    useEffect(() => {
        if (process.env.NODE_ENV === 'production') return undefined;
        window.__mockRecentSkeleton = (on = true) => setMockSkeleton(!!on);
        return () => { delete window.__mockRecentSkeleton; };
    }, []);

    const pushSetToCloud = useCallback(async (set) => {
        if (!isAuthenticated || !window.electron?.library?.savePlannerSet) return false;
        try {
            // refreshAndRetry: a 15-min access token can expire mid-session;
            // without it an expired-JWT 401 rendered as a bogus "offline" tag.
            const result = await refreshAndRetry((token) =>
                window.electron.library.savePlannerSet(token, set.id, setToServerPayload(set))
            );
            return { ok: !(result && result.error), code: result?.error?.code || null };
        } catch {
            return { ok: false, code: null };
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated || !accessToken) return;
        if (cloudSetsLoadedRef.current) return; // once per session — token refreshes must not refetch
        if (!window.electron?.library?.getPlannerSets) return;
        let cancelled = false;
        setSetsHydrating(true);
        (async () => {
            const result = await refreshAndRetry((token) =>
                window.electron.library.getPlannerSets(token)
            ).catch(() => null);
            if (cancelled || !result || result.error || !Array.isArray(result.data)) return;
            cloudSetsLoadedRef.current = true;
            let merged = result.data.map(serverRowToSet);
            const cloudIds = new Set(merged.map(s => s.id));
            // One-time upload of pre-cloud local saves, plus retry of offline saves.
            const alreadyMigrated = localStorage.getItem(SETS_MIGRATED_KEY) === '1';
            const toPush = loadSavedSets()
                .filter(s => (s.setPath || []).length > 0)
                .filter(s => s.pendingSync || (!alreadyMigrated && !cloudIds.has(s.id)))
                .map(s => ({ ...s, id: s.id || genSetId() }));
            for (const s of toPush) {
                if (cancelled) return;
                const { ok, code } = await pushSetToCloud(s);
                const { pendingSync, ...clean } = s;
                // A NEW set the free cap refused is not "offline", it is over
                // the limit: drop it instead of keeping a device-only copy that
                // would retry forever and quietly exceed the 3-set rule.
                if (!ok && code === 'FREE_SET_LIMIT') {
                    merged = merged.filter(c => c.id !== s.id);
                    continue;
                }
                // Pushed sets join the cloud list; failed ones stay cached
                // with pendingSync so the next session retries them.
                merged = [ok ? clean : s, ...merged.filter(c => c.id !== s.id)];
            }
            localStorage.setItem(SETS_MIGRATED_KEY, '1');
            if (cancelled) return;
            persistSavedSets(merged);
            setSavedSets(merged);
        })().finally(() => setSetsHydrating(false));
        return () => { cancelled = true; };
    }, [isAuthenticated, accessToken, pushSetToCloud]);

    // Persist the current canvas as saved set `id` named `name` — local cache
    // synchronously, cloud in the background. Single write path shared by the
    // save dialog, autosave, and the pre-reset flush.
    const persistSnapshot = useCallback((id, name, details) => {
        // Details default to the live set's cover/description; the save dialog
        // passes freshly edited values explicitly (its state hasn't flushed
        // into currentSet* yet at call time).
        const cover = details?.cover !== undefined ? details.cover : currentSetCover;
        const description = details?.description !== undefined ? details.description : currentSetDescription;
        const snapshot = {
            id,
            name,
            date: new Date().toISOString(),
            cover: cover || null,
            description: (description || '').trim() || null,
            nodes,
            nodeOrder,
            setPath,
            activeNodeId,
            // pan/zoom deliberately NOT saved: they describe the saving
            // device's viewport, and loading recomputes a fit for the
            // current screen (see handleLoadSet).
        };
        setSavedSets(prev => {
            // Replace by id; also absorb any legacy (id-less) entry sharing the name.
            const updated = [
                snapshot,
                ...prev.filter(s => (s.id ? s.id !== id : s.name !== name)),
            ].slice(0, 50);
            persistSavedSets(updated);
            return updated;
        });
        savedSigRef.current = makeSetSignature(nodes, nodeOrder, setPath, activeNodeId);
        setSaveStatus('saving');
        (async () => {
            const { ok, code } = await pushSetToCloud(snapshot);
            if (ok) {
                setSaveStatus('saved');
            } else if (code === 'FREE_SET_LIMIT') {
                // Main/server refused a NEW set past the free cap (the UI
                // pre-check was stale or bypassed): drop the optimistic local
                // copy so the list matches the cloud, and make the ask.
                setSavedSets(prev => {
                    const rest = prev.filter(x => x.id !== id);
                    persistSavedSets(rest);
                    return rest;
                });
                setCurrentSetId(null);
                setCurrentSetName('');
                setSaveStatus(null);
                trackEvent('free_limit_hit', { feature: 'set_planner', kind: 'sets', sets_count: FREE_SET_LIMIT, from: 'server' });
                requireUpgrade();
            } else {
                setSavedSets(prev => {
                    const marked = prev.map(s => (s.id === id ? { ...s, pendingSync: true } : s));
                    persistSavedSets(marked);
                    return marked;
                });
                // Guests have no cloud — their local save is the whole story.
                setSaveStatus(isAuthenticated ? 'offline' : 'saved');
            }
        })();
    }, [nodes, nodeOrder, setPath, activeNodeId, pushSetToCloud, isAuthenticated, currentSetCover, currentSetDescription, requireUpgrade]);

    // Save (⌘S / toolbar button), Ableton-style: a named set is overwritten
    // silently — no dialog. Only a never-named set opens the name dialog.
    const handleSaveClick = useCallback(() => {
        if (setPath.length === 0) return;
        if (currentSetId) {
            persistSnapshot(currentSetId, currentSetName);
            trackEvent('planner_set_saved', {
                track_count: setPath.length,
                is_cloud: !!(isAuthenticated && accessToken),
                has_cover: !!currentSetCover,
                has_description: !!(currentSetDescription || '').trim(),
            });
        } else {
            if (!canCreateNewSet(savedSets.length, canAccess)) {
                trackEvent('free_limit_hit', { feature: 'set_planner', kind: 'sets', sets_count: savedSets.length });
                requireUpgrade();
                return;
            }
            setSaveName('');
            setSaveCover(null);
            setSaveDescription('');
            setEditSetTarget(null);
            setSaveAsMode(false);
            setShowSaveDialog(true);
        }
    }, [setPath.length, currentSetId, currentSetName, persistSnapshot, isAuthenticated, accessToken, currentSetCover, currentSetDescription, savedSets.length, canAccess, requireUpgrade]);

    // Save As (⌘⇧S): fork the current set under a new name. For a never-named
    // set this is just the regular first save.
    const handleSaveAsClick = useCallback(() => {
        if (setPath.length === 0) return;
        if (!canCreateNewSet(savedSets.length, canAccess)) {
            trackEvent('free_limit_hit', { feature: 'set_planner', kind: 'sets', sets_count: savedSets.length });
            requireUpgrade();
            return;
        }
        setSaveName(currentSetId ? `${currentSetName} copy` : '');
        setSaveCover(currentSetCover);
        setSaveDescription(currentSetDescription);
        setEditSetTarget(null);
        setSaveAsMode(Boolean(currentSetId));
        setShowSaveDialog(true);
    }, [setPath.length, currentSetId, currentSetName, currentSetCover, currentSetDescription, savedSets.length, canAccess, requireUpgrade]);

    // File → Save Set / Save Set As… (Electron). The menu accelerators consume
    // ⌘S/⌘⇧S in the main process, so these events are the Electron trigger
    // path. Only the active tab is mounted, so other tabs never react.
    useEffect(() => {
        const offSave = window.electron?.planner?.onSaveSet?.(() => {
            handleSaveClick();
        });
        const offSaveAs = window.electron?.planner?.onSaveSetAs?.(() => {
            handleSaveAsClick();
        });
        return () => { offSave?.(); offSaveAs?.(); };
    }, [handleSaveClick, handleSaveAsClick]);

    // ⌘S / ⌘⇧S keydown fallback for web/mobile, where there's no application
    // menu (in Electron the accelerator eats the keystroke first, so this
    // never double-fires).
    useEffect(() => {
        const onKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
                e.preventDefault();
                if (e.shiftKey) handleSaveAsClick();
                else handleSaveClick();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [handleSaveClick, handleSaveAsClick]);
    const [confirmAction, setConfirmAction] = useState(null); // { type, message, onConfirm }
    const [menuNodeId, setMenuNodeId] = useState(null);
    // When the "..." menu would clip off the bottom of the canvas, flip it above
    // the node instead. Default is down; only flips up when there isn't room.
    const [menuOpenUp, setMenuOpenUp] = useState(false);
    const [maybeLater, setMaybeLater] = useState([]);
    const [maybeLaterOpen, setMaybeLaterOpen] = useState(false);
    // Non-null when the last fetch fell back to (or attempted) library-based
    // recommendations because Mixpedia had nothing for the track.
    const [localFallbackNotice, setLocalFallbackNotice] = useState(null);
    // Track whose "Add to Playlist" modal is open (null = closed).
    const [playlistModalTrack, setPlaylistModalTrack] = useState(null);
    const [libTipNodeId, setLibTipNodeId] = useState(null);
    const [libTipPos, setLibTipPos] = useState({ x: 0, y: 0 });
    const [addTrackQuery, setAddTrackQuery] = useState('');
    const [showAddTrack, setShowAddTrack] = useState(false);
    const [addTrackResults, setAddTrackResults] = useState([]);
    const [libraryRecTracks, setLibraryRecTracks] = useState([]); // recs that match library
    const [addTrackPlaylists, setAddTrackPlaylists] = useState([]);
    const [expandedPlaylistId, setExpandedPlaylistId] = useState(null);
    const [expandedPlaylistTracks, setExpandedPlaylistTracks] = useState([]);
    const addTrackRef = useRef(null);
    const addTrackInputRef = useRef(null);
    // "From Library" dropdown: one box over the local library (desktop) and
    // the user's playlists (client-side over lazily loaded playlist tracks,
    // playlist name shown as meta); playlist accordion while the box is empty.
    const [addTrackPlaylistTracks, setAddTrackPlaylistTracks] = useState({}); // playlistId → tracks[]
    const playlistTracksLoadingRef = useRef(false);
    // "From Mixpedia" — separate dropdown over the server-side track catalog,
    // for tracks the user doesn't own yet. Kept apart from From Library on
    // purpose (a unified box was tried 2026-08-17 and rejected).
    const [showAddMixpedia, setShowAddMixpedia] = useState(false);
    const [addMixpediaQuery, setAddMixpediaQuery] = useState('');
    const [addMixpediaResults, setAddMixpediaResults] = useState([]);
    const [addMixpediaLoading, setAddMixpediaLoading] = useState(false);
    const addMixpediaRef = useRef(null);
    const addMixpediaInputRef = useRef(null);
    // Phone add-track sheet: which tab is showing.
    const [addSheetTab, setAddSheetTab] = useState('mixpedia'); // 'mixpedia' | 'playlists'
    const maybeLaterRef = useRef(maybeLater);
    // Monotonic token for fetchRecommendations. Picking a second track before the
    // first's async fetch resolves would otherwise let the stale fetch add its rec
    // nodes AFTER the new selection already cleared the column — leaving two rec
    // columns overlapping in the same slot. Each call captures the current token;
    // once a newer call bumps it, the older one bails before mutating the graph.
    const recReqIdRef = useRef(0);
    maybeLaterRef.current = maybeLater;

    // Background DJPal token resolution — see useDjpalConnect for rationale.
    const { connectFailed, tokenError, retry: retryConnect } = useDjpalConnect();

    // Load Set Planner's recent-searches list. Mixpedia uses its own surface
    // and loads independently in DJPalTab.
    useEffect(() => {
        if (isAuthenticated && !historyLoaded) {
            dispatch(fetchSearchHistory({ surface: 'planner', limit: 30 }));
        }
    }, [dispatch, isAuthenticated, historyLoaded]);

    // Measures the height of the music player overlaying the canvas.
    // The .music-player-panel (rendered by react-jinke-music-player) is
    // position:fixed at the bottom, so the canvas clientHeight includes
    // the occluded area — subtract it to get true visible space.
    // NOTE: .music-player-container is position:relative and sits *below*
    // the canvas in flow (never overlaps), so we must query the panel.
    const getBottomOcclusion = useCallback(() => {
        if (typeof document === 'undefined') return 0;
        const canvasEl = canvasRef.current;
        if (!canvasEl) return 0;
        const canvasRect = canvasEl.getBoundingClientRect();
        const panel = document.querySelector('.music-player-panel');
        if (!panel) return 0;
        const panelRect = panel.getBoundingClientRect();
        // A hidden / not-yet-laid-out panel measures 0×0 at the viewport
        // origin — its "top" of 0 would count the ENTIRE canvas as occluded
        // (availH collapses → zoom slams to the floor, content pins to the
        // top with a dead bottom half). Same defense for any measurement
        // claiming the player covers most of the canvas: no real player bar
        // does, so treat it as bogus and fall back to the static default.
        if (panelRect.height <= 0) return 0;
        const overlap = Math.max(0, canvasRect.bottom - panelRect.top);
        if (overlap > canvasRect.height * 0.5) return 0;
        // Add a small buffer so rec labels don't kiss the player's top edge.
        return overlap > 0 ? overlap + 16 : 0;
    }, []);

    // Compute a zoom + pan that makes the active node + its rec column
    // visible. On desktop we stay at zoom=1.0 unless the rec column is
    // genuinely taller than the viewport — only then do we zoom out just
    // enough to fit top + bottom. Mobile always scales to fit.
    // Returns { zoom, panX, panY } or null if we can't compute.
    const computeBranchFit = useCallback((activeNode, recNodes) => {
        if (!canvasRef.current || !activeNode) return null;
        const canvasW = canvasRef.current.clientWidth;
        const canvasH = canvasRef.current.clientHeight;
        if (!canvasW || !canvasH) return null;

        // Bounding box of active + recs (artwork + label area).
        const xs = [activeNode.x, activeNode.x + NODE_W];
        const ys = [activeNode.y, activeNode.y + NODE_H];
        recNodes.forEach((n) => {
            xs.push(n.x, n.x + NODE_W);
            ys.push(n.y, n.y + NODE_H);
        });
        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);
        const contentW = maxX - minX;
        const contentH = maxY - minY;

        const isNarrow = canvasW <= 768;
        const padX = Math.max(24, canvasW * 0.04);
        // The zoom-control stack overlays the canvas's right edge on mobile
        // — exactly where the fit right-aligns the rec column, hiding the
        // choose-track "+" badges behind the buttons. Reserve its width.
        // Ignore nonsense measurements (hidden stack / covering half the
        // canvas) the same way getBottomOcclusion does.
        let padRight = padX;
        if (isNarrow) {
            const ctrl = document.querySelector('.canvas-zoom-controls');
            if (ctrl) {
                const ctrlRect = ctrl.getBoundingClientRect();
                const canvasRect = canvasRef.current.getBoundingClientRect();
                const occ = canvasRect.right - ctrlRect.left;
                if (ctrlRect.height > 0 && occ > 0 && occ < canvasW * 0.4) {
                    padRight = Math.max(padX, occ + 12);
                }
            }
        }
        // Vertical padding: the music player is position:fixed over the
        // bottom of the canvas, and its height varies per device
        // (safe-area insets differ between iPhone models). Measure actual
        // overlap instead of guessing a static px value. Fall back to a
        // generous default if the player hasn't mounted yet.
        const measuredBottom = getBottomOcclusion();
        const padTop = isNarrow ? 24 : Math.max(8, canvasH * 0.02);
        const padBottom = isNarrow
            ? (measuredBottom || 120)
            : Math.max(8, canvasH * 0.02);
        const availW = Math.max(1, canvasW - padX - padRight);
        const availH = Math.max(1, canvasH - padTop - padBottom);

        let targetZoom;
        if (isNarrow) {
            const zoomFit = Math.min(availW / contentW, availH / contentH, 1.2);
            targetZoom = Math.max(0.4, zoomFit);
        } else {
            // Desktop: prefer full size, but shrink just enough to fit
            // vertically if the rec column would otherwise trim top/bottom.
            //
            // Floor matches mobile's 0.4 — NOT the 0.75 it used to be. A
            // 5-rec column is 740 world px tall (4·CHILD_GAP_Y + NODE_H), so
            // 0.75 only fits a canvas taller than ~578px. Below that the
            // clamp overrode the fit and the branch overflowed, clipping the
            // top and bottom rec behind the toolbar and the player — the fit
            // button and a fresh branch both looked broken.
            //
            // Short canvases are ordinary, not an edge case: measured on a
            // maximised window on a 1080p panel at 150% Windows scaling with
            // the app's own zoom pref at 70%, the canvas is 468px, needing
            // zoom 0.607. Nothing here is Windows-specific — the in-app zoom
            // setting alone can do it on any platform, which is why the fix
            // is not platform-gated. A roomy Mac window cleared 578px, which
            // is why this only ever showed up on short viewports.
            //
            // zoomFit is already "just enough", so the lower floor only binds
            // on genuinely tiny canvases; anything tall keeps the 1.0 branch.
            const zoomFit = Math.min(availH / contentH, 1.0);
            targetZoom = Math.max(0.4, zoomFit);
        }

        // Horizontal positioning: if the whole chain from the first track
        // (START_X) to the new branch fits on screen, show it all with a
        // little left padding — that preserves earlier-track context. If it
        // doesn't fit, right-align the branch so the earlier tracks stay as
        // visible as possible rather than getting shoved fully off-screen.
        const chainW = (maxX - START_X) * targetZoom;
        const targetPanX = chainW <= availW
            ? padX - START_X * targetZoom
            : canvasW - padRight - maxX * targetZoom;

        // Center content in the VISIBLE area (between the top pad and the
        // player-occluded bottom), not the full canvas. Otherwise content
        // gets centered behind the player on mobile — empty space above,
        // bottom rec trimmed.
        const visibleCenterY = (padTop + (canvasH - padBottom)) / 2;
        const contentCenterY = (minY + maxY) / 2;
        const targetPanY = visibleCenterY - contentCenterY * targetZoom;

        return { zoom: targetZoom, panX: targetPanX, panY: targetPanY };
    }, [getBottomOcclusion]);

    // v2: fit the active track + its picks to the viewport the first time the
    // canvas has something to show (the review: "canvas not fit-to-view on
    // load; the right half is empty"). Once per mount; later moves are the
    // user's (and fetchRecommendations' own minimal nudge).
    const v2FitDoneRef = useRef(false);
    useEffect(() => {
        if (!v2 || v2FitDoneRef.current || !activeNodeId) return undefined;
        const active = nodes[activeNodeId];
        if (!active) return undefined;
        const recs = nodeOrder.map(nid => nodes[nid]).filter(n => n && n.parentId === activeNodeId && n.isRecommendation);
        if (!recs.length) return undefined;
        v2FitDoneRef.current = true;
        const raf = requestAnimationFrame(() => {
            const fit = computeBranchFit(active, recs);
            if (fit) { dispatch(setZoom(fit.zoom)); dispatch(setPan({ x: fit.panX, y: fit.panY })); }
        });
        return () => cancelAnimationFrame(raf);
    }, [v2, activeNodeId, nodes, nodeOrder, computeBranchFit, dispatch]);

    // Local-library recommendation fallback. When Mixpedia has nothing for a
    // track (the common case for underground/own-library tracks), suggest
    // compatible tracks from the user's analyzed library. Ranking priority:
    // BPM closeness first, then same genre, then harmonic key. Returns
    // { recs, notice } — recs may be empty (then only the guidance notice shows).
    const getLocalRecommendations = useCallback(async (parentNode, foundInMixpedia = false) => {
        const t = (parentNode && parentNode.track) || {};
        const seedTitle = stripAudioExt(t.title || '').toLowerCase().trim();
        const seedArtist = (t.artist || '').toLowerCase().trim();

        // Load the analyzed library up front — we use it to resolve the seed's
        // own BPM/key too (library tracks are often saved with a null title and
        // may live under a different folder than the seed, so getByPath / a
        // title search alone miss them).
        let all = [];
        try { all = (await window.electron.db.tracks.getAll()) || []; } catch { /* ignore */ }

        const rowFp = (row) => row.filePath || row.file || row.file_path || (row.track && row.track.filePath);
        const rowTitle = (row) => stripAudioExt(getTrackTitle(row) || (row.track || row).title || '').toLowerCase().trim();
        const rowArtist = (row) => ((row.track || row).artist || '').toLowerCase().trim();

        // 1) Resolve the seed's bpm/key/genre: node → same file path → same
        //    artist+title in the library.
        let seedBpm = t.bpm, seedKey = t.key, seedGenre = t.genre;
        const absorb = (rt) => {
            if (!rt) return;
            seedBpm = seedBpm || rt.bpm;
            seedKey = seedKey || rt.key;
            seedGenre = seedGenre || rt.genre || (rt.genres && rt.genres[0]) || (rt.styles && rt.styles[0]);
        };
        if (!seedBpm && t.filePath) {
            try { const row = await window.electron.db.tracks.getByPath(t.filePath); absorb(row?.track || row); } catch { /* ignore */ }
        }
        if (!seedBpm) {
            const hit = all.find((row) =>
                (row.track || row).bpm && (
                    (t.filePath && rowFp(row) === t.filePath) ||
                    (seedArtist && rowArtist(row) === seedArtist && (!seedTitle || rowTitle(row) === seedTitle))
                ));
            absorb(hit && (hit.track || hit));
        }

        // Mixpedia is the good stuff — its picks come from real DJ-set co-plays
        // (tracks DJs actually mix together). Local BPM/key matching is the
        // rougher fallback. The wording leads with that.
        const WHY = 'Mixpedia recommendations come from real DJ sets — tracks DJs actually play together.';
        const CONTINUE = ' Continue from Mixpedia any time.';
        const lead = foundInMixpedia
            ? "Mixpedia knows this track but hasn't seen it played in a set yet"
            : "This track isn't in Mixpedia yet";

        // Need at least a BPM to rank (BPM-first).
        if (!seedBpm) {
            return { recs: [], notice: `${lead}. ${WHY} It also has no analyzed BPM/key in your library, so there's nothing to match locally — analyze it in Library or add the next track manually.${CONTINUE}` };
        }

        // 2) Rank the library by BPM closeness, then genre, then harmonic key.
        const st = store.getState();
        const setKeys = new Set();
        (st?.whatsNext?.setPath || []).forEach((id) => {
            const n = st.whatsNext.nodes[id];
            if (n?.track) setKeys.add(`${(n.track.artist || '').toLowerCase()}|||${stripAudioExt(n.track.title || '').toLowerCase()}`);
        });

        const candidates = [];
        for (const row of all) {
            const rt = row.track || row;
            const fp = rowFp(row);
            const title = stripAudioExt(getTrackTitle(row) || rt.title || '');
            const artist = (rt.artist || '').trim();
            const bpm = rt.bpm;
            if (!title || !bpm) continue;
            if (setKeys.has(`${artist.toLowerCase()}|||${title.toLowerCase()}`)) continue;
            if (fp && t.filePath && fp === t.filePath) continue;
            // Also drop the seed itself when matched by artist+title (folder differs).
            if (seedArtist && artist.toLowerCase() === seedArtist && title.toLowerCase() === seedTitle) continue;
            const dr = bpmDiffRatio(seedBpm, bpm);
            if (dr > 0.08) continue; // within ~8% (half/double-time aware)
            const bpmTier = dr <= 0.03 ? 0 : dr <= 0.06 ? 1 : 2;
            const genreMatch = (seedGenre && rt.genre && rt.genre.toLowerCase() === String(seedGenre).toLowerCase()) ? 1 : 0;
            const kScore = keyCompatScore(seedKey, rt.key);
            candidates.push({ rt, fp, title, artist, bpm, dr, bpmTier, genreMatch, kScore });
        }

        candidates.sort((a, b) =>
            a.bpmTier - b.bpmTier || b.genreMatch - a.genreMatch || b.kScore - a.kScore || a.dr - b.dr);
        const top = candidates.slice(0, 5);

        if (top.length === 0) {
            return { recs: [], notice: `${lead}. ${WHY} And no library tracks are close enough in BPM to suggest — add the next track manually.${CONTINUE}` };
        }

        const recs = top.map((c) => ({
            id: c.fp || undefined,
            title: c.title,
            artist: c.artist,
            // No node artwork (grey placeholder); the player fetches local art.
            artwork: c.rt.cover || c.rt.artwork || null,
            previewUrl: filePathToAudioUrl(c.fp),
            bpm: c.bpm,
            key: c.rt.key || null,
            genre: c.rt.genre || null,
            genres: c.rt.genres || null,
            styles: c.rt.styles || null,
            releaseDate: c.rt.releaseDate || null,
            label: c.rt.label || null,
            filePath: c.fp,
            _inLibrary: true,
            _local: true,
            // No match-score badge for library picks (they aren't Mixpedia's
            // 0–1 co-play score — showing "300%" was meaningless).
            score: null,
        }));
        return { recs, notice: `${lead}. ${WHY} Meanwhile, here are picks from your library matched by BPM, then genre, then key — a rougher starting point.${CONTINUE}` };
    }, [store]);

    // Fetch recommendations for a given track name
    const fetchRecommendations = useCallback(async (trackName, parentNode, excludeKeys = null, overrideView = null) => {
        const myReqId = ++recReqIdRef.current;
        dispatch(setLoading(true));
        dispatch(setError(null));
        setLocalFallbackNotice(null);
        setLockedPreview(null);
        try {
            // Waits out an in-flight background connection (cold start)
            // instead of failing fast with zero recommendations.
            const token = await ensureToken(dispatch, store.getState);

            // Defensive: never send a filename extension to Mixpedia — a query
            // like "Tonmacher - Fuckin Move.wav" won't match the catalog track.
            const searchName = stripAudioExt(trackName);
            // Seed by id when the node carries one (rec nodes and id-bearing
            // history seeds do) — same {trackName, trackId} contract as
            // searchTrack; the name rides along for display/fallback only.
            const seedId = entityId(parentNode?.track) || null;
            const seed = seedId ? { trackName: searchName, trackId: seedId } : searchName;
            const quotaKey = { planId: planIdRef.current, nodeId: parentNode?.id || 'seed' };
            let result = await window.electron.djpal.plannerRecommendations(token, seed, quotaKey);
            // Cold start: main hasn't resolved the tier yet — wait it out
            // instead of failing the step (same shape the feed used to need).
            for (let attempt = 1; attempt <= 4 && result?.error === 'TIER_NOT_READY'; attempt++) {
                await new Promise((r) => setTimeout(r, 300 * attempt));
                if (recReqIdRef.current !== myReqId) return;
                result = await window.electron.djpal.plannerRecommendations(token, seed, quotaKey);
            }
            // The server-side quota (music-info-server) answers inside `data`:
            // lift a lock or a quota to the top level so every surface handles
            // one shape (desktop main already does this; web/native get it raw).
            {
                const body = result && !result.error ? (result.data || result) : null;
                if (body?.locked) result = { error: 'FREE_LIMIT_REACHED', locked: true, moreCount: body.moreCount || 0, quota: body.quota || null };
                else if (body?.quota && result.quota === undefined) result = { ...result, quota: body.quota };
            }
            // Web/native have no main process: apply the same step rule locally
            // (renderer-only there, by design — see plannerLimits).
            if (result && result.quota === undefined && !canAccess && !result.error) {
                const local = localPlannerQuota(authUser?.id, quotaKey);
                const count = ((result.data || result).relatedTracks || []).length;
                result = local.allowed
                    ? { ...result, quota: local }
                    : { error: 'FREE_LIMIT_REACHED', locked: true, moreCount: count, quota: local };
            }
            if (recReqIdRef.current !== myReqId) return;
            if (result && result.quota !== undefined) setFreeQuota(result.quota);
            if (result?.error === 'FREE_LIMIT_REACHED') {
                setLockedPreview({ moreCount: result.moreCount || 0, parentId: parentNode?.id || null });
                trackEvent('free_limit_hit', {
                    feature: 'set_planner',
                    kind: 'steps',
                    steps_used: result.quota?.used ?? FREE_STEP_LIMIT,
                    more_count: result.moreCount || 0,
                });
                dispatch(setLoading(false));
                return;
            }

            if (result.error) throw new Error(result.error);

            // A newer selection superseded this fetch while it was in flight —
            // stop before enriching the parent or adding any rec nodes.
            if (recReqIdRef.current !== myReqId) return;

            const data = result.data || result;

            // Enrich parent node with trackInfo from API — fill in any missing fields
            // (artwork, audio sources, metadata) so the seed node becomes playable.
            const trackInfo = data.result?.track || null;
            const parentTrack = parentNode?.track;
            if (trackInfo && parentNode) {
                const updates = {};
                // Fill-if-empty: text-seeded nodes (typed tracks, old saved
                // sets) adopt the catalog id the server resolved, so every
                // later expansion/save is id-based. Never overwrite an
                // existing id.
                if (!entityId(parentTrack) && entityId(trackInfo)) updates.id = entityId(trackInfo);
                if (!getArtwork(parentTrack) && getArtwork(trackInfo)) updates.artwork = getArtwork(trackInfo);
                if (!parentTrack?.artist && trackInfo.artist) {
                    updates.artist = trackInfo.artist;
                    if (trackInfo.title) updates.title = trackInfo.title;
                }
                if (!getPreviewUrl(parentTrack) && getPreviewUrl(trackInfo)) updates.previewUrl = getPreviewUrl(trackInfo);
                if (!parentTrack?.youtubeId && trackInfo.youtubeId) updates.youtubeId = trackInfo.youtubeId;
                if (!parentTrack?.soundcloudUrl && trackInfo.soundcloudUrl) updates.soundcloudUrl = trackInfo.soundcloudUrl;
                if (!parentTrack?.bpm && trackInfo.bpm) updates.bpm = trackInfo.bpm;
                if (!parentTrack?.key && trackInfo.key) updates.key = trackInfo.key;
                if (!parentTrack?.genre && trackInfo.genre) updates.genre = trackInfo.genre;
                if (!parentTrack?.genres && trackInfo.genres) updates.genres = trackInfo.genres;
                if (!parentTrack?.styles && trackInfo.styles) updates.styles = trackInfo.styles;
                if (!parentTrack?.releaseDate && trackInfo.releaseDate) updates.releaseDate = trackInfo.releaseDate;
                if (!parentTrack?.label && trackInfo.label) updates.label = trackInfo.label;
                if (!parentTrack?.artists && trackInfo.artists) updates.artists = trackInfo.artists;
                if (!parentTrack?.remixers && trackInfo.remixers) updates.remixers = trackInfo.remixers;
                if (Object.keys(updates).length > 0) {
                    dispatch(updateNodeTrack({ id: parentNode.id, track: updates }));
                }
            }

            const allRecs = (data.relatedTracks || []).map(r => r.track || r);

            // Deduplicate by title+artist AND exclude tracks already in set
            const normalize = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
            const makeKey = (artist, title) => `${normalize(artist)}-${normalize(title)}`;
            const seen = new Set();
            const uniqueRecs = [];
            const currentSetKeys = new Set();
            const state = window.__reduxStore?.getState();
            if (state?.whatsNext?.setPath) {
                state.whatsNext.setPath.forEach(id => {
                    const n = state.whatsNext.nodes[id];
                    if (n?.track) {
                        currentSetKeys.add(makeKey(n.track.artist, n.track.title));
                    }
                });
            }
            // Also exclude the track we're searching for
            if (trackInfo) {
                currentSetKeys.add(makeKey(trackInfo.artist, trackInfo.title));
            }
            // Exclude previously shown recommendations (on refresh)
            if (excludeKeys) {
                excludeKeys.forEach(k => currentSetKeys.add(k));
            }
            // Exclude tracks saved in maybe later
            for (const ml of maybeLaterRef.current) {
                currentSetKeys.add(makeKey(ml.artist, ml.title));
            }

            for (const rec of allRecs) {
                const key = makeKey(rec.artist, rec.title);
                if (!seen.has(key) && !currentSetKeys.has(key)) {
                    seen.add(key);
                    uniqueRecs.push(rec);
                }
            }

            // Split into with-audio and without-audio (YouTube/SC count as having audio)
            const hasAnyAudio = (r) => getPreviewUrl(r) || (r.youtubeId && r.youtubeId !== '-1') || r.soundcloudUrl;
            const withPreview = uniqueRecs.filter(r => hasAnyAudio(r));
            const withoutPreview = uniqueRecs.filter(r => !hasAnyAudio(r));

            // Shuffle top pool for variety — pick 5 randomly from top 15
            const shuffle = (arr) => {
                const a = [...arr];
                for (let i = a.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [a[i], a[j]] = [a[j], a[i]];
                }
                return a;
            };

            // Build candidate pool — wider pool gives more variety on refresh
            let pool = withPreview.length >= 5 ? withPreview.slice(0, 25) : [...withPreview, ...withoutPreview];

            // Check library matches on the full pool BEFORE selecting top 5
            const cleanTitleFn = (s) => (s || '').replace(/\.\w{2,4}$/, '').toLowerCase().trim();
            const libraryMatchKeys = new Set();
            try {
                const poolMatchPromises = pool.map(async (rec) => {
                    try {
                        if (!rec.title || rec.title.length < 2) return null;
                        const filters = { limit: 3 };
                        if (rec.artist) filters.artist = rec.artist;
                        if (rec.title) filters.title = rec.title;
                        const results = await window.electron.db.tracks.search(filters);
                        const match = results?.some(r => {
                            const libTitle = cleanTitleFn(r.track?.title || r.track?.trackName || r.title || '');
                            const libArtist = (r.track?.artist || r.artist || '').toLowerCase().trim();
                            return libTitle === cleanTitleFn(rec.title) && (!rec.artist || libArtist.includes(rec.artist.toLowerCase()));
                        });
                        return match ? `${(rec.artist || '').toLowerCase()}|||${(rec.title || '').toLowerCase()}` : null;
                    } catch { return null; }
                });
                const matches = await Promise.all(poolMatchPromises);
                matches.filter(Boolean).forEach(k => libraryMatchKeys.add(k));
            } catch { /* non-critical */ }

            // Mark library matches on pool
            pool.forEach(rec => {
                rec._inLibrary = libraryMatchKeys.has(`${(rec.artist || '').toLowerCase()}|||${(rec.title || '').toLowerCase()}`);
            });

            // Select 5: library matches first, then shuffle the rest
            const libMatches = pool.filter(r => r._inLibrary);
            const nonLibMatches = pool.filter(r => !r._inLibrary);
            let selected;
            if (libMatches.length >= 5) {
                selected = shuffle(libMatches).slice(0, 5);
            } else {
                selected = [...libMatches, ...shuffle(nonLibMatches).slice(0, 5 - libMatches.length)];
            }

            // Dead end — walk back through set path trying previous tracks
            if (selected.length === 0) {
                const currentState = window.__reduxStore?.getState();
                const currentPath = currentState?.whatsNext?.setPath || [];
                // Try each previous node in reverse order
                for (let i = currentPath.length - 2; i >= 0; i--) {
                    const prevId = currentPath[i];
                    const prevNode = currentState?.whatsNext?.nodes?.[prevId];
                    if (!prevNode) continue;

                    const prevTrackName = prevNode.track.artist
                        ? `${prevNode.track.artist} - ${prevNode.track.title}`
                        : prevNode.track.title;

                    try {
                        // Same node as the step above, so the quota counts nothing new.
                        const fallbackResult = await window.electron.djpal.plannerRecommendations(
                            token, prevTrackName, { planId: planIdRef.current, nodeId: parentNode?.id || 'seed' }
                        );
                        const fallbackData = fallbackResult.data || fallbackResult;
                        const fallbackRecs = (fallbackData.relatedTracks || []).map(r => r.track || r);
                        const fallbackSeen = new Set();
                        const fallbackUnique = [];
                        for (const rec of fallbackRecs) {
                            const key = makeKey(rec.artist, rec.title);
                            if (!fallbackSeen.has(key) && !currentSetKeys.has(key)) {
                                fallbackSeen.add(key);
                                fallbackUnique.push(rec);
                            }
                        }
                        const fbHasAudio = (r) => getPreviewUrl(r) || (r.youtubeId && r.youtubeId !== '-1') || r.soundcloudUrl;
                        const fbWithPreview = fallbackUnique.filter(r => fbHasAudio(r));
                        const fbWithout = fallbackUnique.filter(r => !fbHasAudio(r));
                        if (fbWithPreview.length >= 5) {
                            selected = fbWithPreview.slice(0, 5);
                        } else if (fbWithPreview.length + fbWithout.length > 0) {
                            selected = [...fbWithPreview, ...fbWithout.slice(0, 5 - fbWithPreview.length)];
                        }
                        if (selected.length > 0) break;
                    } catch (e) {
                        // try next previous track
                    }
                }

                if (selected.length === 0) {
                    // Mixpedia has nothing (even walking back the set) — fall
                    // back to compatible tracks from the user's own library.
                    // `trackInfo` truthy means Mixpedia knows the track but has
                    // no set data (vs. the track being absent entirely).
                    const local = await getLocalRecommendations(parentNode, !!trackInfo);
                    setLocalFallbackNotice(local.notice);
                    if (local.recs.length > 0) {
                        selected = local.recs;
                    } else {
                        dispatch(setLoading(false));
                        return;
                    }
                }
            }

            // Final staleness check before mutating the graph: the fallback
            // walk-back and local-library paths above each await, so re-check
            // here — this is the single point where all paths add their nodes.
            if (recReqIdRef.current !== myReqId) return;

            // Store matched tracks for the "From Library" dropdown
            const matchedTracks = selected.filter(r => r._inLibrary);
            if (matchedTracks.length > 0) setLibraryRecTracks(matchedTracks);

            const parentX = parentNode.x;
            const parentY = parentNode.y;
            const level = (parentNode.level || 0) + 1;
            const totalHeight = (selected.length - 1) * CHILD_GAP_Y;
            const startChildY = parentY - totalHeight / 2;

            const newNodeIds = [];
            selected.forEach((rec, i) => {
                const id = genId();
                newNodeIds.push(id);
                const inLibrary = !!rec._inLibrary;
                dispatch(addNode({
                    id,
                    track: {
                        // Carry the track's public id so the sample→YouTube
                        // lazy-resolve in resolveTrackSrc can actually fire.
                        // Without it, resolveTrackSrc has no id to resolve/
                        // write back and silently falls straight to the 30s
                        // sample (the "EdOne / Inception plays sample
                        // immediately" bug).
                        id: entityId(rec),
                        title: rec.title,
                        artist: rec.artist,
                        artists: rec.artists || null,
                        remixers: rec.remixers || null,
                        artwork: getArtwork(rec),
                        previewUrl: getPreviewUrl(rec),
                        duration: rec.duration ?? null,
                        youtubeId: rec.youtubeId || null,
                        soundcloudUrl: rec.soundcloudUrl || null,
                        bpm: rec.bpm,
                        key: rec.key,
                        genre: rec.genre,
                        genres: rec.genres,
                        styles: rec.styles,
                        releaseDate: rec.releaseDate,
                        label: rec.label,
                        score: rec.score,
                        inLibrary,
                    },
                    parentId: parentNode.id,
                    childIds: [],
                    x: parentX + LEVEL_GAP_X,
                    y: startChildY + i * CHILD_GAP_Y,
                    level,
                    isRecommendation: true,
                    selected: false,
                }));
            });

            dispatch(setRecommendations(newNodeIds));
            dispatch(setLoading(false));

            // Synthesize the rec nodes we just positioned (Redux update
            // hasn't flushed yet by the time we compute the fit).
            const recNodesForFit = selected.map((_, i) => ({
                x: parentX + LEVEL_GAP_X,
                y: startChildY + i * CHILD_GAP_Y,
            }));
            const canvasW = canvasRef.current?.clientWidth || 0;
            const canvasH = canvasRef.current?.clientHeight || 0;
            if (canvasW && canvasH) {
                const curZoom = overrideView?.zoom ?? zoom;
                const curPanX = overrideView?.panX ?? panX;
                const curPanY = overrideView?.panY ?? panY;

                // Branch bounding box in world coords (active + new recs).
                const bMinX = parentNode.x;
                const bMaxX = parentNode.x + LEVEL_GAP_X + NODE_W;
                const bMinY = Math.min(parentNode.y, startChildY);
                const bMaxY = Math.max(
                    parentNode.y + NODE_H,
                    startChildY + (selected.length - 1) * CHILD_GAP_Y + NODE_H,
                );

                // Does the branch already fit at the current pan+zoom?
                const left = bMinX * curZoom + curPanX;
                const right = bMaxX * curZoom + curPanX;
                const top = bMinY * curZoom + curPanY;
                const bottom = bMaxY * curZoom + curPanY;
                const padX = Math.max(24, canvasW * 0.04);
                const isNarrow = canvasW <= 768;
                const measuredBottom = getBottomOcclusion();
                const padTop = isNarrow ? 24 : Math.max(8, canvasH * 0.02);
                const padBottom = isNarrow
                    ? (measuredBottom || 120)
                    : Math.max(8, canvasH * 0.02);
                const fitsAlready =
                    left >= padX && right <= canvasW - padX
                    && top >= padTop && bottom <= canvasH - padBottom;

                if (!fitsAlready) {
                    // Check if current zoom is enough to hold the branch at all.
                    const branchW = (bMaxX - bMinX) * curZoom;
                    const branchH = (bMaxY - bMinY) * curZoom;
                    const fitsHoriz = branchW <= canvasW - padX * 2;
                    const fitsVert = branchH <= canvasH - padTop - padBottom;

                    if (fitsHoriz && fitsVert) {
                        // Same zoom works — just nudge pan minimally so the
                        // branch comes into view without yanking earlier
                        // tracks all the way off the left. When we have to
                        // shift because the right edge ran out, leave a bit
                        // of extra room on the right so the next recs have
                        // breathing space and don't immediately need another
                        // shift on the following click.
                        const rightBreathing = Math.max(50, canvasW * 0.10);
                        let newPanX = curPanX;
                        if (right > canvasW - padX) {
                            newPanX = canvasW - padX - rightBreathing - bMaxX * curZoom;
                        } else if (left < padX) {
                            newPanX = padX - bMinX * curZoom;
                        }
                        let newPanY = curPanY;
                        if (bottom > canvasH - padBottom) {
                            newPanY = canvasH - padBottom - bMaxY * curZoom;
                        } else if (top < padTop) {
                            newPanY = padTop - bMinY * curZoom;
                        }
                        if (newPanX !== curPanX || newPanY !== curPanY) {
                            dispatch(setPan({ x: newPanX, y: newPanY }));
                        }
                    } else {
                        // Branch won't fit at current zoom — full recompute
                        // (scales down on mobile, nudges on desktop).
                        const fit = computeBranchFit(parentNode, recNodesForFit);
                        if (fit) {
                            dispatch(setZoom(fit.zoom));
                            dispatch(setPan({ x: fit.panX, y: fit.panY }));
                        }
                    }
                }
            }

        } catch (err) {
            console.error('[WhatsNext] fetch error:', err);
            // Don't clobber a newer in-flight fetch's loading/error state.
            if (recReqIdRef.current !== myReqId) return;
            dispatch(setError(errMsg(err)));
            dispatch(setLoading(false));
        }
    }, [dispatch, store, zoom, panX, panY, computeBranchFit, getBottomOcclusion, getLocalRecommendations, canAccess, authUser]);

    // Set Planner stores its recents under the `planner` surface — separate
    // from Mixpedia's `mixpedia` surface so the two lists don't cross-pollute.
    // Bare typed Enter queries get a synthetic publicId of the form
    // "q:artist - title" so they dedupe correctly against later canonical
    // entries.
    const recordRecentSearch = useCallback(({ title, artist, artwork, publicId }) => {
        if (!isAuthenticated || !title) return;
        dispatch(addSearchHistoryEntry({
            entityType: 'track',
            entityPublicId: publicId || `q:${((artist || '').trim() ? `${artist.trim()} - ${title.trim()}` : title.trim()).toLowerCase()}`,
            entityName: title,
            entitySubtitle: artist || null,
            entityArtwork: artwork || null,
            surface: 'planner',
        }));
    }, [dispatch, isAuthenticated]);

    // Start flow with a track (internal — always executes)
    const doStart = useCallback(async (query, trackMeta) => {
        const trimmed = (query || '').trim();
        if (!trimmed) return;
        dispatch(resetFlow());
        nodeIdCounter = 0;
        // Fresh set — no saved identity or baseline yet.
        setCurrentSetId(null);
        setCurrentSetName('');
        setCurrentSetCover(null);
        setCurrentSetDescription('');
        setSaveStatus(null);
        savedSigRef.current = null;
        planIdRef.current = genSetId();
        setFreeQuota(null);
        setLockedPreview(null);

        const canvasH = canvasRef.current?.clientHeight || 600;
        const startY = Math.round(canvasH / 2 + 20);

        const startId = genId();
        const track = trackMeta
            ? {
                // Catalog id when the seed source had one (history rows,
                // Mixpedia hand-offs) — recommendations then seed by id.
                id: trackMeta.publicId || null,
                title: stripAudioExt(trackMeta.title || trimmed),
                artist: trackMeta.artist || '',
                // Leave artwork empty for local seeds — the node shows the grey
                // placeholder; Mixpedia enrichment fills real art when found,
                // and the bottom player pulls local art at play time.
                artwork: trackMeta.artwork || null,
                // Local-file seeds have no online preview — make them playable
                // (and show the play button) via the local audio server.
                previewUrl: trackMeta.previewUrl || filePathToAudioUrl(trackMeta.filePath) || null,
                // Preserve local-file identity + analyzed metadata for the
                // library recommendation fallback (see fetchRecommendations).
                filePath: trackMeta.filePath || null,
                bpm: trackMeta.bpm ?? null,
                key: trackMeta.key ?? null,
                genre: trackMeta.genre ?? null,
                duration: trackMeta.duration ?? trackMeta.durationSec ?? null,
            }
            : { title: trimmed, artist: '', artwork: null, previewUrl: null };
        const startNode = {
            id: startId,
            track,
            parentId: null,
            childIds: [],
            x: START_X,
            y: startY,
            level: 0,
            isRecommendation: false,
            selected: true,
        };
        dispatch(addNode(startNode));
        dispatch(setActiveNode(startId));
        dispatch(addToSetPath(startId));
        dispatch(setPan({ x: 0, y: 0 }));
        dispatch(setZoom(1));

        await fetchRecommendations(trimmed, startNode, null, { zoom: 1, panX: 0, panY: 0 });
    }, [dispatch, fetchRecommendations]);

    // Picking a suggestion / recent on the hero fills the input and primes the
    // CTA — the user confirms by tapping "Let's Go". The track metadata is
    // stashed so handleStart below can reuse it without refetching.
    const [pendingStart, setPendingStart] = useState(null);

    // Start flow — confirm if there's existing work
    const handleStart = useCallback((overrideQuery, trackMeta) => {
        // overrideQuery may be a SyntheticEvent when wired directly to onClick;
        // accept only string overrides, fall back to the input value otherwise.
        const rawInput = (typeof overrideQuery === 'string' ? overrideQuery : searchQuery).trim();
        let q = rawInput;
        let meta = trackMeta;
        // If we have a pending pick whose display text matches what's in the
        // input, reuse its stashed query + metadata. The display form uses an
        // em-dash while the server query uses a hyphen, so we compare against
        // pendingStart.display.
        const usingPending = !meta && pendingStart && pendingStart.display === rawInput;
        if (usingPending) {
            q = pendingStart.query;
            meta = pendingStart.meta;
        }
        if (!q) return;
        window.electron.analytics.track('whats_next_start', { query: q });
        // Record in recents so the user can re-launch. For suggestion/recent
        // picks we already have a real publicId; for plain typed Enter we
        // log a placeholder keyed by the query string.
        if (usingPending && pendingStart.historyEntry) {
            recordRecentSearch(pendingStart.historyEntry);
        } else if (!meta) {
            const dashIdx = q.indexOf(' - ');
            const artist = dashIdx > 0 ? q.slice(0, dashIdx).trim() : '';
            const title = dashIdx > 0 ? q.slice(dashIdx + 3).trim() : q;
            recordRecentSearch({ title, artist, artwork: null, publicId: null });
        }
        setPendingStart(null);
        if (isCurrentSetDirty()) {
            if (currentSetId) {
                setConfirmAction({
                    title: 'Save your set?',
                    message: `Save changes to "${currentSetName}" before starting a new set?`,
                    confirmLabel: 'Discard changes',
                    onConfirm: () => { setConfirmAction(null); doStart(q, meta); },
                    saveLabel: 'Save & start new',
                    onSave: () => {
                        setConfirmAction(null);
                        persistSnapshot(currentSetId, currentSetName);
                        doStart(q, meta);
                    },
                });
            } else {
                setConfirmAction({
                    message: 'Starting a new set will discard your unsaved set. Continue?',
                    onConfirm: () => { setConfirmAction(null); doStart(q, meta); },
                });
            }
            return;
        }
        doStart(q, meta);
    }, [searchQuery, isCurrentSetDirty, doStart, recordRecentSearch, pendingStart, currentSetId, currentSetName, persistSnapshot]);

    // Consume an externally-seeded start (e.g. dispatched by TrackContextMenu
    // when the user picks "Start a set with this track" from a playlist or
    // search row). handleStart runs the same confirmation flow as starting
    // from inside the planner.
    const incomingStart = useSelector((state) => state.whatsNext.pendingStart);
    useEffect(() => {
        if (!incomingStart || !incomingStart.query) return;
        handleStart(incomingStart.query, incomingStart.meta);
        dispatch(clearPendingStart());
    }, [incomingStart, handleStart, dispatch]);

    const fetchTrackSuggestions = useCallback(async (q) => {
        const token = localStorage.getItem('djpal:token');
        if (!token) return [];
        try {
            const result = await window.electron.djpal.autocompleteTrack(token, q);
            if (result.error || !result.results) return [];
            return result.results.map(t => ({
                title: t.artist ? `${t.artist} – ${t.title}` : t.title,
                subtitle: null,
                artworkUrl: t.artworkUrl || null,
                searchQuery: t.artist ? `${t.artist} - ${t.title}` : t.title,
                rawTitle: t.title,
                rawArtist: t.artist || null,
                id: entityId(t),
                duration: t.duration ?? null,
            }));
        } catch { return []; }
    }, []);

    const handleSuggestionSelect = useCallback((suggestion) => {
        const display = suggestion.title || suggestion.searchQuery || '';
        setSearchQuery(display);
        setPendingStart({
            display,
            query: suggestion.searchQuery,
            meta: {
                title: suggestion.rawTitle || suggestion.title,
                artist: suggestion.rawArtist || '',
                artwork: suggestion.artworkUrl || null,
                publicId: suggestion.id || null,
                duration: suggestion.duration ?? null,
            },
            historyEntry: {
                title: suggestion.rawTitle || suggestion.title,
                artist: suggestion.rawArtist,
                artwork: suggestion.artworkUrl,
                publicId: suggestion.id,
            },
        });
        // v2: picking a track IS the start. "Let's go" stays for typed queries.
        if (v2) {
            recordRecentSearch({
                title: suggestion.rawTitle || suggestion.title,
                artist: suggestion.rawArtist,
                artwork: suggestion.artworkUrl,
                publicId: suggestion.id,
            });
            handleStart(suggestion.searchQuery, {
                title: suggestion.rawTitle || suggestion.title,
                artist: suggestion.rawArtist || '',
                artwork: suggestion.artworkUrl || null,
                publicId: suggestion.id || null,
                duration: suggestion.duration ?? null,
            });
        }
    }, [v2, handleStart, recordRecentSearch]);

    const handleRecentSelect = useCallback((entry) => {
        const title = entry.entityName || '';
        if (!title) return;
        const artist = entry.entitySubtitle || '';
        const query = artist ? `${artist} - ${title}` : title;
        const display = artist ? `${artist} – ${title}` : title;
        setSearchQuery(display);
        setPendingStart({
            display,
            query,
            meta: {
                title,
                artist,
                artwork: entry.entityArtwork || null,
                publicId: entry.entityPublicId && !String(entry.entityPublicId).startsWith('q:') ? entry.entityPublicId : null,
            },
            historyEntry: {
                title,
                artist,
                artwork: entry.entityArtwork,
                publicId: entry.entityPublicId && !String(entry.entityPublicId).startsWith('q:') ? entry.entityPublicId : null,
            },
        });
        if (v2) {
            const publicId = entry.entityPublicId && !String(entry.entityPublicId).startsWith('q:') ? entry.entityPublicId : null;
            recordRecentSearch({ title, artist, artwork: entry.entityArtwork, publicId });
            handleStart(query, { title, artist, artwork: entry.entityArtwork || null, publicId });
        }
    }, [v2, handleStart, recordRecentSearch]);

    const handleRecentDismiss = useCallback((id) => {
        dispatch(deleteSearchHistoryEntry(id));
    }, [dispatch]);

    const handleRecentClear = useCallback(() => {
        dispatch(clearSearchHistoryByType({ entityType: 'track', surface: 'planner' }));
    }, [dispatch]);

    // Select a recommendation node
    const handleSelectRec = useCallback(async (nodeId) => {
        const node = nodes[nodeId];
        if (!node || !node.isRecommendation) return;

        // Read position/parent from the current graph before the recommendation
        // nodes are cleared below.
        const parentNode = node.parentId ? nodes[node.parentId] : null;
        const recPosition = (parentNode?.childIds || [])
            .filter(cid => nodes[cid]?.isRecommendation)
            .indexOf(nodeId);
        trackEvent('recommendation_accepted', {
            source_track_id: parentNode?.track?.id ?? null,
            suggested_track_id: node.track?.id ?? null,
            ...(recPosition >= 0 ? { position: recPosition } : {}),
        });

        setRecentlySelectedId(nodeId);
        setTimeout(() => setRecentlySelectedId(prev => prev === nodeId ? null : prev), 1500);

        dispatch(removeRecommendationNodes([nodeId, ...setPath]));
        dispatch(markNodeSelected(nodeId));
        dispatch(setActiveNode(nodeId));
        dispatch(addToSetPath(nodeId));

        const trackName = node.track.artist
            ? `${node.track.artist} - ${node.track.title}`
            : node.track.title;
        await fetchRecommendations(trackName, node);
    }, [nodes, setPath, dispatch, fetchRecommendations]);

    // Add track to maybe later list
    const handleMaybeLater = useCallback((track) => {
        const normalize = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const key = `${normalize(track.artist)}-${normalize(track.title)}`;
        setMaybeLater(prev => {
            if (prev.some(t => `${normalize(t.artist)}-${normalize(t.title)}` === key)) return prev;
            return [...prev, track];
        });
        setMenuNodeId(null);
        toast.success('Saved for later');
    }, [toast]);

    // Use a maybe-later track — add it to the set as if it were a recommendation
    const handleUseMaybeLater = useCallback(async (track, index) => {
        if (!activeNodeId) return;
        const activeNode = nodes[activeNodeId];
        if (!activeNode) return;

        // Remove current recommendation nodes
        dispatch(removeRecommendationNodes([...setPath]));

        // Create a new node for this track
        const id = genId();
        const parentX = activeNode.x;
        const parentY = activeNode.y;
        const level = (activeNode.level || 0) + 1;

        dispatch(addNode({
            id,
            track: { ...track },
            parentId: activeNodeId,
            childIds: [],
            x: parentX + LEVEL_GAP_X,
            y: parentY,
            level,
            isRecommendation: false,
            selected: true,
        }));
        dispatch(setActiveNode(id));
        dispatch(addToSetPath(id));

        // Remove from maybe later
        setMaybeLater(prev => prev.filter((_, i) => i !== index));

        // Fetch recs for the new track — fetchRecommendations handles pan/zoom
        // via its own fitsAlready check so we don't pre-empt with a jump here.
        const trackName = track.artist
            ? `${track.artist} - ${track.title}`
            : track.title;
        await fetchRecommendations(trackName, { id, x: parentX + LEVEL_GAP_X, y: parentY, level, track });
    }, [activeNodeId, nodes, setPath, dispatch, fetchRecommendations]);

    // Load playlists when add-track dropdown opens. Web reads cloud-synced
    // playlists from user-library-service; desktop reads from local SQLite.
    // Same pattern Crate Digger (SmartRecsTab) uses.
    useEffect(() => {
        if (!showAddTrack || addTrackPlaylists.length > 0) return;
        if (isWeb) {
            if (!accessToken) return;
            refreshAndRetry((token) => window.electron.library.getPlaylists(token))
                .then(result => {
                    if (result?.error) return; // transient — retried on next open
                    const data = result?.data || [];
                    setAddTrackPlaylists(data.map(p => ({ ...p, trackCount: p.track_count })));
                })
                .catch(() => {});
        } else {
            // includeTracks — the "In your playlists" search section filters
            // these client-side (same call the DJ-app sidebars make).
            window.electron.db.playlists.getAll(true)
                .then(all => {
                    const list = all || [];
                    setAddTrackPlaylists(list);
                    const map = {};
                    for (const pl of list) if (Array.isArray(pl.tracks)) map[pl.id] = pl.tracks;
                    setAddTrackPlaylistTracks(prev => ({ ...prev, ...map }));
                })
                .catch(() => {});
        }
    }, [showAddTrack, isWeb, accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

    // Web: playlist tracks aren't in the list payload — pull them lazily
    // (once, all playlists, small parallel batches) the first time the user
    // types a query, so "In your playlists" can match.
    useEffect(() => {
        if (!isWeb || !showAddTrack || !accessToken) return;
        if (addTrackQuery.trim().length < MIN_SEARCH_CHARS) return;
        if (playlistTracksLoadingRef.current) return;
        const missing = addTrackPlaylists.filter(pl => !addTrackPlaylistTracks[pl.id]);
        if (!missing.length) return;
        playlistTracksLoadingRef.current = true;
        let cancelled = false;
        (async () => {
            const loaded = {};
            for (let i = 0; i < missing.length; i += 4) {
                const batch = missing.slice(i, i + 4);
                await Promise.all(batch.map(async (pl) => {
                    try {
                        const result = await refreshAndRetry((token) => window.electron.library.getPlaylist(token, pl.id));
                        if (result?.error) return;
                        const data = result?.data || result;
                        loaded[pl.id] = data?.tracks || [];
                    } catch { /* skip this playlist */ }
                }));
                if (cancelled) return;
            }
            setAddTrackPlaylistTracks(prev => ({ ...prev, ...loaded }));
        })().finally(() => { playlistTracksLoadingRef.current = false; });
        return () => { cancelled = true; };
    }, [isWeb, showAddTrack, accessToken, addTrackQuery, addTrackPlaylists, addTrackPlaylistTracks]);

    // "In your playlists" — substring match on artist/title across every
    // loaded playlist; one row per distinct track with the playlist name(s)
    // as meta.
    const addTrackPlaylistMatches = useMemo(() => {
        const q = addTrackQuery.trim().toLowerCase();
        if (q.length < MIN_SEARCH_CHARS) return [];
        const seen = new Map();
        for (const pl of addTrackPlaylists) {
            const tracks = addTrackPlaylistTracks[pl.id];
            if (!Array.isArray(tracks)) continue;
            for (const t of tracks) {
                const artist = t.track?.artist || t.artist || '';
                const title = getTrackTitle(t) || '';
                if (!`${artist} ${title}`.toLowerCase().includes(q)) continue;
                const key = `${artist}|||${title}`.toLowerCase();
                const hit = seen.get(key);
                if (hit) { if (!hit.playlistNames.includes(pl.name)) hit.playlistNames.push(pl.name); continue; }
                seen.set(key, { track: t, playlistNames: [pl.name] });
                if (seen.size >= 10) break;
            }
            if (seen.size >= 10) break;
        }
        return [...seen.values()];
    }, [addTrackQuery, addTrackPlaylists, addTrackPlaylistTracks]);

    // Search library tracks as user types (debounced — shares the app-wide
    // autocomplete threshold so every search surface stays in lockstep).
    useEffect(() => {
        if (!addTrackQuery.trim() || addTrackQuery.trim().length < MIN_SEARCH_CHARS) {
            setAddTrackResults([]);
            return;
        }
        const search = window.electron.db?.tracks?.search; // desktop only — web has no local library
        if (typeof search !== 'function') { setAddTrackResults([]); return; }
        const timer = setTimeout(async () => {
            try {
                const results = await search({ searchTerm: addTrackQuery.trim(), limit: 10 });
                setAddTrackResults((results || []).slice(0, 10));
            } catch { setAddTrackResults([]); }
        }, 350);
        return () => clearTimeout(timer);
    }, [addTrackQuery]);

    // Load playlist tracks when expanded. Same web/desktop split as the
    // playlist list itself: cloud on web, local SQLite on desktop.
    useEffect(() => {
        if (!expandedPlaylistId) { setExpandedPlaylistTracks([]); return; }
        const cached = addTrackPlaylistTracks[expandedPlaylistId];
        if (Array.isArray(cached)) { setExpandedPlaylistTracks(cached); return; }
        if (isWeb) {
            if (!accessToken) { setExpandedPlaylistTracks([]); return; }
            refreshAndRetry((token) => window.electron.library.getPlaylist(token, expandedPlaylistId))
                .then(result => {
                    if (result?.error) { setExpandedPlaylistTracks([]); return; }
                    const data = result?.data || result;
                    const tracks = data?.tracks || [];
                    setExpandedPlaylistTracks(tracks);
                    setAddTrackPlaylistTracks(prev => ({ ...prev, [expandedPlaylistId]: tracks }));
                })
                .catch(() => setExpandedPlaylistTracks([]));
        } else {
            window.electron.db.playlists.getTracks(expandedPlaylistId)
                .then(tracks => {
                    setExpandedPlaylistTracks(tracks || []);
                    setAddTrackPlaylistTracks(prev => ({ ...prev, [expandedPlaylistId]: tracks || [] }));
                })
                .catch(() => setExpandedPlaylistTracks([]));
        }
    }, [expandedPlaylistId, isWeb, accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

    // Close dropdown on outside click (desktop; the phone sheet has its own overlay)
    useEffect(() => {
        if (!showAddTrack || checkIsMobileWeb()) return;
        const handleClick = (e) => {
            if (addTrackRef.current && !addTrackRef.current.contains(e.target)) {
                setShowAddTrack(false);
                setAddTrackQuery('');
                setExpandedPlaylistId(null);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [showAddTrack]);

    // Search Mixpedia track autocomplete as the user types
    useEffect(() => {
        if (!showAddMixpedia) return undefined;
        const q = addMixpediaQuery.trim();
        if (q.length < MIN_SEARCH_CHARS) { setAddMixpediaResults([]); setAddMixpediaLoading(false); return undefined; }
        let cancelled = false;
        const timer = setTimeout(async () => {
            setAddMixpediaLoading(true);
            try {
                const results = await fetchTrackSuggestions(q);
                if (!cancelled) setAddMixpediaResults(results || []);
            } finally {
                if (!cancelled) setAddMixpediaLoading(false);
            }
        }, 300);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [addMixpediaQuery, showAddMixpedia, fetchTrackSuggestions]);

    // Close Mixpedia dropdown on outside click (desktop only)
    useEffect(() => {
        if (!showAddMixpedia || checkIsMobileWeb()) return undefined;
        const handleClick = (e) => {
            if (addMixpediaRef.current && !addMixpediaRef.current.contains(e.target)) {
                setShowAddMixpedia(false);
                setAddMixpediaQuery('');
                setAddMixpediaResults([]);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [showAddMixpedia]);

    // Add a track manually by name (e.g. from library)
    const handleAddTrack = useCallback(async () => {
        const raw = addTrackQuery.trim();
        if (!raw) return;
        if (!activeNodeId) return;
        const activeNode = nodes[activeNodeId];
        if (!activeNode) return;

        // Parse "Artist - Title" or just "Title"
        const dashIdx = raw.indexOf(' - ');
        const artist = dashIdx > -1 ? raw.slice(0, dashIdx).trim() : '';
        const title = dashIdx > -1 ? raw.slice(dashIdx + 3).trim() : raw;

        dispatch(removeRecommendationNodes([...setPath]));

        const id = genId();
        const parentX = activeNode.x;
        const parentY = activeNode.y;
        const level = (activeNode.level || 0) + 1;

        dispatch(addNode({
            id,
            track: { title, artist, artwork: null, previewUrl: null },
            parentId: activeNodeId,
            childIds: [],
            x: parentX + LEVEL_GAP_X,
            y: parentY,
            level,
            isRecommendation: false,
            selected: true,
        }));
        dispatch(setActiveNode(id));
        dispatch(addToSetPath(id));

        setAddTrackQuery('');
        setShowAddTrack(false);

        // fetchRecommendations handles pan/zoom via its own fitsAlready check.
        await fetchRecommendations(raw, { id, x: parentX + LEVEL_GAP_X, y: parentY, level, track: { title, artist } });
    }, [addTrackQuery, activeNodeId, nodes, setPath, dispatch, fetchRecommendations]);

    // Add a library track object to the set
    const handleAddLibraryTrack = useCallback(async (trackObj) => {
        if (!activeNodeId) return;
        const activeNode = nodes[activeNodeId];
        if (!activeNode) return;

        const artist = trackObj.track?.artist || trackObj.artist || '';
        const title = getTrackTitle(trackObj);
        // Local DB rows use `cover`; cloud playlist tracks use `artwork`.
        const artwork = trackObj.track?.cover || trackObj.cover || trackObj.artwork || trackObj.track?.artwork || null;
        const filePath = trackObj.filePath || trackObj.file || trackObj.track?.filePath || null;
        // Catalog id when the row carries one (cloud playlist rows, enriched
        // library rows via djpalId) — null for purely local files. Keeping it
        // on the node makes rec-seeding and saved sets id-based, not text.
        // NEVER read `.id` here: library/playlist rows carry their own integer
        // row id there, and a foreign integer must not become an entity id.
        const catalogId = trackObj.track?.djpalId || trackObj.djpalId
            || trackObj.track?.track_public_id || trackObj.track_public_id
            || trackObj.track?.publicId || trackObj.publicId || null;
        // Seconds from the local DB row (music-metadata) or "m:ss" from a
        // catalog row — feeds the estimated set length; null when unknown.
        const duration = trackObj.track?.duration ?? trackObj.duration ?? trackObj.durationSec ?? null;

        // Pre-check: the file_path stored in the DB may be stale if the user
        // moved/renamed the file. We still add the track to the set (the user
        // may want it for set planning) — just skip the playable URL so the
        // play button doesn't render and the player can't get stuck on a 404.
        let audioUrl = filePathToAudioUrl(filePath);
        let fileMissing = false;
        if (audioUrl && filePath && !/^(https?:|online:)/.test(filePath)) {
            try {
                const missing = await window.electron.db.tracks.checkMissingFiles([filePath]);
                if (missing && missing.length > 0) {
                    audioUrl = null;
                    fileMissing = true;
                    toast.warning('Track file is missing — added without preview');
                }
            } catch { /* non-fatal */ }
        }

        dispatch(removeRecommendationNodes([...setPath]));

        const id = genId();
        const parentX = activeNode.x;
        const parentY = activeNode.y;
        const level = (activeNode.level || 0) + 1;

        dispatch(addNode({
            id,
            track: { id: catalogId, title, artist, artwork, previewUrl: audioUrl, filePath, fileMissing, duration },
            parentId: activeNodeId,
            childIds: [],
            x: parentX + LEVEL_GAP_X,
            y: parentY,
            level,
            isRecommendation: false,
            selected: true,
        }));
        dispatch(setActiveNode(id));
        dispatch(addToSetPath(id));

        setShowAddTrack(false);
        setAddTrackQuery('');
        setExpandedPlaylistId(null);

        // fetchRecommendations handles pan/zoom via its own fitsAlready check.
        const searchName = artist ? `${artist} - ${title}` : title;
        await fetchRecommendations(searchName, { id, x: parentX + LEVEL_GAP_X, y: parentY, level, track: { id: catalogId, title, artist, artwork } });
    }, [activeNodeId, nodes, setPath, dispatch, fetchRecommendations, toast]);

    // Add a Mixpedia track (server-side catalog) to the set. Mirrors
    // handleAddLibraryTrack but pulls metadata from an autocomplete suggestion
    // instead of a local DB row, so there's no filePath/audio source — the
    // node renders without a play button.
    const handleAddMixpediaTrack = useCallback(async (suggestion) => {
        if (!activeNodeId) return;
        const activeNode = nodes[activeNodeId];
        if (!activeNode) return;

        const artist = suggestion.rawArtist || '';
        const title = suggestion.rawTitle || suggestion.title || '';
        const artwork = suggestion.artworkUrl || null;
        if (!title) return;

        dispatch(removeRecommendationNodes([...setPath]));

        const id = genId();
        const parentX = activeNode.x;
        const parentY = activeNode.y;
        const level = (activeNode.level || 0) + 1;

        // Autocomplete suggestions carry the catalog id — keep it on the node
        // so recommendations seed by id (not text) and the id survives in
        // saved sets.
        const catalogId = suggestion.id || entityId(suggestion) || null;
        dispatch(addNode({
            id,
            track: { id: catalogId, title, artist, artwork, previewUrl: null, duration: suggestion.duration ?? null },
            parentId: activeNodeId,
            childIds: [],
            x: parentX + LEVEL_GAP_X,
            y: parentY,
            level,
            isRecommendation: false,
            selected: true,
        }));
        dispatch(setActiveNode(id));
        dispatch(addToSetPath(id));

        setShowAddMixpedia(false);
        setAddMixpediaQuery('');
        setAddMixpediaResults([]);
        // Phone: the sheet is keyed on showAddTrack — close it too.
        setShowAddTrack(false);
        setAddTrackQuery('');

        // fetchRecommendations handles pan/zoom via its own fitsAlready check.
        const searchName = artist ? `${artist} - ${title}` : title;
        await fetchRecommendations(searchName, { id, x: parentX + LEVEL_GAP_X, y: parentY, level, track: { id: catalogId, title, artist, artwork } });
    }, [activeNodeId, nodes, setPath, dispatch, fetchRecommendations]);

    // Play/pause preview — uses shared resolveTrackSrc (YT full → SC full →
    // lazy YT search for sample-only → sample fallback). Same flow as
    // DJPalAudioPreview and MusicPlayer's autoplay path.
    const handlePlayPreview = useCallback(async (e, track, nodeId) => {
        e.stopPropagation();
        startPlayTiming('setplanner-tap');

        // If this node is already playing, pause it.
        if (playingNodeId === nodeId && autoPlay) {
            if (window.musicPlayerInstance) {
                window.musicPlayerInstance.pause();
            }
            return;
        }

        setLoadingNodeId(nodeId);
        try {
            const resolved = await resolveTrackSrc(track);
            if (!resolved) {
                toast.error('Unable to play this track');
                return;
            }
            dispatch(resetAudioList());
            dispatch(setAutoRadio(false));
            dispatch(clearListContext());
            dispatch(addTrack({
                name: track.title || 'Unknown Track',
                singer: track.artist || '',
                // Grey placeholder in the node, but the player shows real art:
                // online artwork if any, else the local file's embedded cover.
                cover: resolveCover(getArtwork(track) || filePathToArtUrl(track.filePath)),
                musicSrc: resolved.musicSrc,
                ...(resolved.isHls ? { isHls: true, hlsSrc: resolved.hlsSrc } : {}),
                ...(resolved.originalUrl ? { originalUrl: resolved.originalUrl } : {}),
            }));
            dispatch(setAutoPlay(true));
            setPlayingNodeId(nodeId);
        } finally {
            setLoadingNodeId(null);
        }
    }, [dispatch, toast, playingNodeId, autoPlay]);

    // --- Node drag handlers ---
    const handleNodeMouseDown = useCallback((e, nodeId) => {
        e.stopPropagation();
        // Right-button opens the node's "···" menu (onContextMenu below) —
        // it must not also start a drag.
        if (e.button === 2) return;
        const node = nodes[nodeId];
        if (!node) return;
        setDraggingNodeId(nodeId);
        setDragOffset({
            x: e.clientX / zoom - node.x - panX / zoom,
            y: e.clientY / zoom - node.y - panY / zoom,
        });
    }, [nodes, zoom, panX, panY]);

    // --- Canvas pan handlers ---
    const handleMouseDown = useCallback((e) => {
        setMenuNodeId(null);
        if (draggingNodeId) return;
        if (e.target === canvasRef.current || e.target === innerRef.current || e.target.classList.contains('connections-svg')) {
            setIsPanning(true);
            setPanStartState({ x: e.clientX - panX, y: e.clientY - panY });
        }
    }, [panX, panY, draggingNodeId]);

    const handleMouseMove = useCallback((e) => {
        if (draggingNodeId) {
            const newX = e.clientX / zoom - dragOffset.x - panX / zoom;
            const newY = e.clientY / zoom - dragOffset.y - panY / zoom;
            dispatch(updateNodePosition({ id: draggingNodeId, x: newX, y: newY }));
            return;
        }
        if (!isPanning) return;
        dispatch(setPan({
            x: e.clientX - panStart.x,
            y: e.clientY - panStart.y,
        }));
    }, [draggingNodeId, dragOffset, zoom, panX, panY, isPanning, panStart, dispatch]);

    const handleMouseUp = useCallback(() => {
        setDraggingNodeId(null);
        setIsPanning(false);
    }, []);

    // --- Touch handlers for mobile ---
    // pinchRef tracks the two-finger pinch gesture: initial distance between
    // touches, initial zoom, and the midpoint in canvas coordinates so we
    // zoom around where the user's fingers are.
    const pinchRef = useRef(null);

    const touchDistance = (t1, t2) => {
        const dx = t1.clientX - t2.clientX;
        const dy = t1.clientY - t2.clientY;
        return Math.sqrt(dx * dx + dy * dy);
    };

    const handleTouchStart = useCallback((e) => {
        if (e.touches.length === 2) {
            // Start pinch. Cancel any in-progress single-finger pan.
            setIsPanning(false);
            setDraggingNodeId(null);
            const [t1, t2] = [e.touches[0], e.touches[1]];
            const rect = canvasRef.current?.getBoundingClientRect();
            const midClientX = (t1.clientX + t2.clientX) / 2;
            const midClientY = (t1.clientY + t2.clientY) / 2;
            // Midpoint in canvas-local coords (pre-zoom/pan) — the stable
            // content point we want to keep under the fingers as zoom changes.
            const localX = rect ? (midClientX - rect.left - panX) / zoom : 0;
            const localY = rect ? (midClientY - rect.top - panY) / zoom : 0;
            pinchRef.current = {
                startDist: touchDistance(t1, t2),
                startZoom: zoom,
                localX,
                localY,
            };
            return;
        }
        if (e.touches.length !== 1) return;
        const touch = e.touches[0];
        const synth = { clientX: touch.clientX, clientY: touch.clientY, target: e.target, stopPropagation: () => {}, classList: e.target.classList };
        handleMouseDown(synth);
    }, [handleMouseDown, panX, panY, zoom]);

    const handleTouchMove = useCallback((e) => {
        if (e.touches.length === 2 && pinchRef.current) {
            e.preventDefault();
            const [t1, t2] = [e.touches[0], e.touches[1]];
            const dist = touchDistance(t1, t2);
            const { startDist, startZoom, localX, localY } = pinchRef.current;
            const ratio = dist / (startDist || 1);
            const newZoom = Math.max(0.3, Math.min(2, startZoom * ratio));
            // Keep the pinch midpoint anchored to the same content coords
            const rect = canvasRef.current?.getBoundingClientRect();
            const midClientX = (t1.clientX + t2.clientX) / 2;
            const midClientY = (t1.clientY + t2.clientY) / 2;
            const targetPanX = rect ? midClientX - rect.left - localX * newZoom : panX;
            const targetPanY = rect ? midClientY - rect.top - localY * newZoom : panY;
            dispatch(setZoom(newZoom));
            dispatch(setPan({ x: targetPanX, y: targetPanY }));
            return;
        }
        if (e.touches.length !== 1) return;
        e.preventDefault();
        const touch = e.touches[0];
        handleMouseMove({ clientX: touch.clientX, clientY: touch.clientY });
    }, [handleMouseMove, panX, panY, dispatch]);

    const handleTouchEnd = useCallback((e) => {
        // If the gesture just dropped below 2 fingers, end the pinch.
        if (e.touches.length < 2 && pinchRef.current) {
            pinchRef.current = null;
        }
        if (e.touches.length === 0) {
            handleMouseUp();
        }
    }, [handleMouseUp]);

    const handleNodeTouchStart = useCallback((e, nodeId) => {
        if (e.touches.length !== 1) return;
        e.stopPropagation();
        const touch = e.touches[0];
        handleNodeMouseDown({ clientX: touch.clientX, clientY: touch.clientY, stopPropagation: () => {} }, nodeId);
    }, [handleNodeMouseDown]);

    // Zoom handler
    const handleWheel = useCallback((e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.05 : 0.05;
        const newZoom = Math.max(0.3, Math.min(2, zoom + delta));
        dispatch(setZoom(newZoom));
    }, [zoom, dispatch]);

    // Undo last track
    const handleUndo = useCallback(async () => {
        if (setPath.length <= 1) return;
        // Get the node that will become active after undo (second-to-last)
        const newActiveId = setPath[setPath.length - 2];
        const newActiveNode = nodes[newActiveId];

        // Remove recommendation nodes before undo
        dispatch(removeRecommendationNodes([...setPath]));
        dispatch(undoLastTrack());

        // Re-fetch recommendations for the new active node
        if (newActiveNode) {
            const trackName = newActiveNode.track.artist
                ? `${newActiveNode.track.artist} - ${newActiveNode.track.title}`
                : newActiveNode.track.title;
            await fetchRecommendations(trackName, newActiveNode);
        }
    }, [setPath, nodes, dispatch, fetchRecommendations]);

    // Refresh recommendations for the active node
    const handleRefreshRecs = useCallback(async () => {
        if (!activeNodeId || isLoading) return;
        const activeNode = nodes[activeNodeId];
        if (!activeNode) return;

        // Collect keys of currently displayed recommendations so we can exclude them
        const normalize = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const prevRecKeys = nodeOrder
            .map(id => nodes[id])
            .filter(n => n?.isRecommendation)
            .map(n => `${normalize(n.track.artist)}-${normalize(n.track.title)}`);

        // Remove current recommendation nodes
        dispatch(removeRecommendationNodes([...setPath]));

        const trackName = activeNode.track.artist
            ? `${activeNode.track.artist} - ${activeNode.track.title}`
            : activeNode.track.title;
        await fetchRecommendations(trackName, activeNode, prevRecKeys);
    }, [activeNodeId, nodes, nodeOrder, setPath, isLoading, dispatch, fetchRecommendations]);

    // Break chain at a specific node (remove it and everything after)
    const handleBreakChain = useCallback(async (nodeId) => {
        const idx = setPath.indexOf(nodeId);
        if (idx <= 0) return;
        const newActiveId = setPath[idx - 1];
        const newActiveNode = nodes[newActiveId];

        // Remove recommendation nodes first
        dispatch(removeRecommendationNodes([...setPath]));
        dispatch(breakChainAt(nodeId));

        // Re-fetch recommendations for the new active node
        if (newActiveNode) {
            const trackName = newActiveNode.track.artist
                ? `${newActiveNode.track.artist} - ${newActiveNode.track.title}`
                : newActiveNode.track.title;
            await fetchRecommendations(trackName, newActiveNode);
        }
    }, [setPath, nodes, dispatch, fetchRecommendations]);

    // Reset (internal — always executes)
    const doReset = useCallback(() => {
        planIdRef.current = null;
        setFreeQuota(null);
        setLockedPreview(null);
        // Closing via the toolbar arrow while the set's own nav entry is on
        // top: step history back to the start-screen stop so the stack matches
        // what's on screen (otherwise the next Back is a visual no-op).
        const { app } = store.getState();
        const top = app.navIndex >= 0 ? app.navStack[app.navIndex] : null;
        if (top && top.plannerSet && app.activeTab === 'whats-next' && !(_isWeb && !_isNative)) {
            dispatch(navigateHistory('back'));
        }
        dispatch(resetFlow());
        nodeIdCounter = 0;
        setSearchQuery('');
        setCurrentSetId(null);
        setCurrentSetName('');
        setCurrentSetCover(null);
        setCurrentSetDescription('');
        setSaveStatus(null);
        savedSigRef.current = null;
    }, [dispatch, store]);

    // Reset — Ableton-style: a dirty NAMED set offers Save/Discard/Cancel; a
    // dirty unnamed scratch set gets a plain discard warning; clean closes
    // silently.
    // `onCancel` lets a caller that already moved state (history back) undo
    // its move when the user keeps the set.
    const handleReset = useCallback((onCancel) => {
        const cancel = typeof onCancel === 'function' ? onCancel : undefined;
        if (!isCurrentSetDirty()) {
            doReset();
            return;
        }
        if (currentSetId) {
            setConfirmAction({
                title: 'Save your set?',
                message: `Save changes to "${currentSetName}" before closing?`,
                confirmLabel: 'Discard changes',
                onConfirm: () => { setConfirmAction(null); doReset(); },
                saveLabel: 'Save & close',
                onSave: () => {
                    setConfirmAction(null);
                    persistSnapshot(currentSetId, currentSetName);
                    doReset();
                },
                onCancel: cancel,
            });
            return;
        }
        setConfirmAction({
            message: 'This will discard your unsaved set. Continue?',
            onConfirm: () => { setConfirmAction(null); doReset(); },
            onCancel: cancel,
        });
    }, [isCurrentSetDirty, doReset, currentSetId, currentSetName, persistSnapshot]);

    // --- App history integration (desktop + native; real web uses browser history) ---
    // Opening a set pushes its own nav entry, so the navbar/mouse/system Back
    // from inside a set lands on the planner start screen — treated exactly
    // like the toolbar's back arrow (close set, offer to save if dirty) —
    // instead of skipping to whatever tab preceded the planner.
    const usesAppNav = !(_isWeb && !_isNative);
    const navTop = useSelector((state) => state.app.navStack[state.app.navIndex] || null);
    const activeTab = useSelector((state) => state.app.activeTab);
    const setNavIdRef = useRef(null);
    const prevHasStartedRef = useRef(hasStarted);
    useEffect(() => {
        const was = prevHasStartedRef.current;
        prevHasStartedRef.current = hasStarted;
        if (!usesAppNav) return;
        if (hasStarted && !was) {
            // Entered a set (search start or load). Push the set stop unless
            // the top already is one (e.g. loading a set from inside a set).
            if (navTop && navTop.plannerSet) { setNavIdRef.current = navTop.navId; return; }
            dispatch(pushNavEntry({ category: 'explore', tab: 'whats-next', plannerSet: true }));
        } else if (!hasStarted && was) {
            setNavIdRef.current = null;
        }
    }, [hasStarted, usesAppNav, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps
    // Learn the navId of the entry we just pushed.
    useEffect(() => {
        if (hasStarted && navTop && navTop.plannerSet && setNavIdRef.current == null) {
            setNavIdRef.current = navTop.navId;
        }
    }, [navTop, hasStarted]);
    // History moved BACK past the set entry onto an older planner stop while a
    // set is open → that's "leave the set". Only older entries count: a fresh
    // planner-tab click from another tab pushes a NEWER planner entry and must
    // return the user to their set untouched.
    const handleResetRef = useRef(handleReset);
    handleResetRef.current = handleReset;
    useEffect(() => {
        if (!usesAppNav || !hasStarted || !navTop) return;
        const setNavId = setNavIdRef.current;
        if (setNavId == null || navTop.plannerSet) return;
        if (activeTab !== 'whats-next' || navTop.tab !== 'whats-next') return;
        if (navTop.navId >= setNavId) return;
        handleResetRef.current(() => { dispatch(navigateHistory('forward')); });
    }, [navTop, activeTab, hasStarted, usesAppNav, dispatch]);

    // --- Save / Load ---
    // Cover upload for the save dialog — same rules as playlist covers, then
    // downscaled so the snapshot stays under the server's data cap.
    const processCoverFile = useCallback((file) => {
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
            toast.warning('Image size should be less than 2MB');
            return;
        }
        if (!file.type.startsWith('image/')) {
            toast.warning('Please select an image file');
            return;
        }
        const reader = new FileReader();
        reader.onload = async (event) => {
            const scaled = await downscaleCoverImage(event.target.result);
            setSaveCover(scaled);
        };
        reader.readAsDataURL(file);
    }, [toast]);

    // Update name/cover/description of a saved set WITHOUT loading it onto the
    // canvas (row edit in the Saved Sets list). The set's snapshot data is
    // kept as-is; only the details change. Same local-first + best-effort
    // cloud push as persistSnapshot.
    const updateSetDetails = useCallback((target, { name, cover, description }) => {
        const updated = {
            ...target,
            id: target.id || genSetId(), // legacy saves get an id on first edit
            name,
            cover: cover || null,
            description: (description || '').trim() || null,
            date: new Date().toISOString(),
        };
        setSavedSets(prev => {
            const next = prev.map(s =>
                (s.id && target.id ? s.id === target.id : s.name === target.name) ? updated : s
            );
            persistSavedSets(next);
            return next;
        });
        // Keep the toolbar chip / live details in sync when editing the set
        // that's open on the canvas.
        if (target.id && currentSetId === target.id) {
            setCurrentSetName(name);
            setCurrentSetCover(updated.cover);
            setCurrentSetDescription(updated.description || '');
        }
        (async () => {
            const ok = await pushSetToCloud(updated);
            if (!ok && isAuthenticated) {
                setSavedSets(prev => {
                    const marked = prev.map(s => (s.id === updated.id ? { ...s, pendingSync: true } : s));
                    persistSavedSets(marked);
                    return marked;
                });
            }
        })();
    }, [currentSetId, pushSetToCloud, isAuthenticated]);

    // Dialog-driven save: first save of an unnamed set, details edit of a
    // named set, asNew=true for an explicit "Save as copy" fork, or a
    // details-only edit of a listed set (editSetTarget).
    const handleSaveSet = useCallback((asNew = false) => {
        const name = saveName.trim();
        if (!name) return;
        if (editSetTarget) {
            updateSetDetails(editSetTarget, { name, cover: saveCover, description: saveDescription });
            trackEvent('planner_set_saved', {
                track_count: (editSetTarget.setPath || []).length,
                is_cloud: !!(isAuthenticated && accessToken),
                has_cover: !!saveCover,
                has_description: !!(saveDescription || '').trim(),
                from: 'details_edit',
            });
            setShowSaveDialog(false);
            setEditSetTarget(null);
            setSaveName('');
            return;
        }
        if (setPath.length === 0) return;
        const isNew = asNew || !currentSetId;
        if (isNew && !canCreateNewSet(savedSets.length, canAccess)) { requireUpgrade(); return; }
        const id = isNew ? genSetId() : currentSetId;
        persistSnapshot(id, name, { cover: saveCover, description: saveDescription });
        trackEvent('planner_set_saved', {
            track_count: setPath.length,
            is_cloud: !!(isAuthenticated && accessToken),
            has_cover: !!saveCover,
            has_description: !!(saveDescription || '').trim(),
        });
        setCurrentSetId(id);
        setCurrentSetName(name);
        setCurrentSetCover(saveCover || null);
        setCurrentSetDescription((saveDescription || '').trim());
        setShowSaveDialog(false);
        setSaveName('');
        if (isNew) toast.success(`Set "${name}" saved`);
    }, [saveName, saveCover, saveDescription, editSetTarget, updateSetDetails, setPath.length, currentSetId, persistSnapshot, toast, isAuthenticated, accessToken, savedSets.length, canAccess, requireUpgrade]);

    const openSavedSets = useCallback(() => {
        setSavedSetsQuery('');
        savedSetsSearchTrackedRef.current = false;
        setShowSavedSets(true);
    }, []);

    useEffect(() => {
        if (showSavedSets && savedSetsQuery.trim() && !savedSetsSearchTrackedRef.current) {
            savedSetsSearchTrackedRef.current = true;
            trackEvent('planner_saved_sets_searched', { sets_count: savedSets.length });
        }
    }, [showSavedSets, savedSetsQuery, savedSets.length]);

    // Entries of { set, matchType, matchLabel }. matchType tells the row WHY
    // the set matched so it can show the evidence (a track/artist match is
    // otherwise invisible — rows only show name/description/first track).
    // Ranked name > description > track so a set literally named what you
    // typed never sits below an incidental track hit.
    // Start-screen "Recent" grid: ALL saved sets, newest-first by last save
    // (the modal adds search on top). Undated legacy local saves sort last but
    // still show — they're the only sets a never-logged-in user has.
    const recentSets = useMemo(() => {
        const ts = (s) => { const t = s.date ? new Date(s.date).getTime() : NaN; return isNaN(t) ? 0 : t; };
        return [...savedSets].sort((a, b) => ts(b) - ts(a));
    }, [savedSets]);
    // One row only (whatever fits the width) — "Browse all" opens the full,
    // searchable list. Measure how many cards fit.
    const [recentCols, setRecentCols] = useState(Infinity);
    const recentGridRef = useCallback((el) => {
        if (recentGridObs.current) { recentGridObs.current.disconnect(); recentGridObs.current = null; }
        if (!el) return;
        // The row is a shrink-to-fit flex-wrap (so it centers under the hero),
        // so its own width can't be measured without a feedback loop —
        // measure the space it MAY use (its max-width) and the real card width.
        const measure = () => {
            const cs = getComputedStyle(el);
            const gap = parseFloat(cs.columnGap) || 0;
            // Usable width = the start screen's content box, capped by the
            // row's own max-width (desktop). Never the row/section itself:
            // both are shrink-to-fit and would feed back into the count.
            const host = el.closest('.whats-next-start');
            const hostW = host
                ? host.clientWidth - parseFloat(getComputedStyle(host).paddingLeft) - parseFloat(getComputedStyle(host).paddingRight)
                : Infinity;
            const avail = Math.min(parseFloat(cs.maxWidth) || Infinity, hostW);
            const card = el.querySelector('.start-recent-card');
            const cardW = card ? card.getBoundingClientRect().width : 0;
            if (!isFinite(avail) || !cardW) { setRecentCols(Infinity); return; }
            // +0.5px tolerance: fractional card widths at non-100% zoom.
            setRecentCols(Math.max(1, Math.floor((avail + gap + 0.5) / (cardW + gap))));
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        const host = el.closest('.whats-next-start');
        if (host) ro.observe(host);
        recentGridObs.current = ro;
    }, []);
    // Render only what fits (not CSS-clipped: hidden cards would still be
    // tab-focusable). The ref callback measures during commit, so the trim
    // lands before paint. Desktop = one row; phones get three rows (two
    // cards each) — a single row of two is too little to be useful there.
    const recentRows = checkIsMobileWeb() ? 3 : 1;
    // Cold path (no mirror to hydrate from): stand-in cards with the real
    // card's footprint, so the loaded row lands exactly where they stood and
    // an empty screen never claims "no saved sets". They carry
    // .start-recent-card so the column measurement above still works with no
    // real cards on screen; 4 is the pre-measurement guess (recentCols is
    // Infinity until the ref callback runs).
    //
    // knownSetCount > 0 is the licence to promise cards at all: an empty
    // mirror looks identical for a user with sets on another machine and for
    // one who has never saved a set, and ghosts that resolve to an empty row
    // are a lie told to exactly the people with the emptiest screen. Unknown
    // (null, e.g. a genuinely new device) shows nothing, as it did before.
    const skeletonBudget = recentRows * (isFinite(recentCols) ? recentCols : 4);
    const showRecentSkeleton = mockSkeleton || (setsHydrating && recentSets.length === 0 && knownSetCount > 0);
    // Exactly as many ghosts as the account is known to have, so three saved
    // sets do not come back as a row of seven.
    const recentSkeletonCount = Math.max(1, Math.min(knownSetCount || skeletonBudget, skeletonBudget, 8));
    const visibleRecentSets = showRecentSkeleton ? [] : ((v2 && v2AllSets) ? recentSets : recentSets.slice(0, recentCols * recentRows));

    const filteredSavedSets = useMemo(() => {
        const q = savedSetsQuery.trim().toLowerCase();
        if (!q) return savedSets.map((set) => ({ set, matchType: null, matchLabel: null }));
        // Artists/remixers arrays come from Mixpedia as strings or {name}
        // objects depending on the endpoint — accept both.
        const names = (list) => (Array.isArray(list) ? list : [])
            .map((a) => (typeof a === 'string' ? a : a?.name || ''));
        const entries = [];
        for (const set of savedSets) {
            if ((set.name || '').toLowerCase().includes(q)) {
                entries.push({ set, matchType: 'name', matchLabel: null, rank: 0 });
                continue;
            }
            if ((set.description || '').toLowerCase().includes(q)) {
                entries.push({ set, matchType: 'description', matchLabel: null, rank: 1 });
                continue;
            }
            for (const id of set.setPath || []) {
                const t = set.nodes?.[id]?.track;
                if (!t) continue;
                const people = [t.artist, ...names(t.artists), ...names(t.remixers)].filter(Boolean);
                const matchedPerson = people.find((n) => n.toLowerCase().includes(q));
                if (matchedPerson || (t.title || '').toLowerCase().includes(q)) {
                    // Lead with the matched collaborator/remixer, not the
                    // primary artist — that's the name the user typed.
                    const who = matchedPerson || t.artist;
                    entries.push({
                        set,
                        matchType: 'track',
                        matchLabel: who ? `${who} — ${t.title || ''}` : (t.title || ''),
                        rank: 2,
                    });
                    break;
                }
            }
        }
        entries.sort((a, b) => a.rank - b.rank); // stable: keeps saved order within each rank
        return entries;
    }, [savedSets, savedSetsQuery]);

    // Pencil on a Saved Sets row — edit details in place, without loading the
    // set. The details dialog stacks on top of the (still open) list.
    const handleEditSetDetails = useCallback((set, e) => {
        e.stopPropagation();
        setEditSetTarget(set);
        setSaveName(set.name || '');
        setSaveCover(set.cover || null);
        setSaveDescription(set.description || '');
        setSaveAsMode(false);
        setShowSaveDialog(true);
    }, []);

    // Background refresh of volatile catalog fields (artwork, playback links)
    // for id-bearing nodes of a just-loaded set — one batched lookup, the
    // likes-view pattern (hydrateLikesWithArtists). The canvas renders from
    // the snapshot first; fresh covers/links swap in when the response lands.
    // Title/artist are the user's plan and are NEVER overwritten; a local
    // file's preview is never clobbered. Purely cosmetic — all failures are
    // silent.
    const hydrateSetFromCatalog = useCallback(async (set) => {
        if (!window.electron?.djpal?.tracksByPublicIds) return;
        const idsByNode = Object.entries(set.nodes || {})
            .map(([nodeId, n]) => [nodeId, entityId(n?.track)])
            // Catalog public ids are non-numeric strings — a bare number here
            // would be a foreign row id that must not reach the catalog API.
            .filter(([, pid]) => typeof pid === 'string' && pid && !/^\d+$/.test(pid));
        if (!idsByNode.length) return;
        try {
            const token = await ensureToken(dispatch, store.getState);
            if (!token) return;
            const res = await window.electron.djpal.tracksByPublicIds(
                token, [...new Set(idsByNode.map(([, pid]) => pid))]
            );
            if (!res || res.error || !Array.isArray(res)) return;
            const byId = new Map();
            for (const t of res) {
                const pid = entityId(t);
                if (pid) byId.set(pid, t);
            }
            // If the user already edited while the lookup was in flight, the
            // set is legitimately dirty — apply the refresh but leave the
            // baseline alone. On an untouched set, fold the refresh into the
            // clean baseline so opening a set never reads as an unsaved edit.
            const pre = store.getState().whatsNext;
            const wasClean = makeSetSignature(pre.nodes, pre.nodeOrder, pre.setPath, pre.activeNodeId) === savedSigRef.current;
            let changed = false;
            for (const [nodeId, pid] of idsByNode) {
                const fresh = byId.get(pid);
                const cur = pre.nodes[nodeId]?.track;
                if (!fresh || !cur) continue;
                const updates = {};
                const art = getArtwork(fresh);
                if (art && art !== getArtwork(cur)) updates.artwork = art;
                if (fresh.youtubeId && fresh.youtubeId !== '-1' && fresh.youtubeId !== cur.youtubeId) updates.youtubeId = fresh.youtubeId;
                if (fresh.soundcloudUrl && fresh.soundcloudUrl !== cur.soundcloudUrl) updates.soundcloudUrl = fresh.soundcloudUrl;
                if (!cur.filePath && !getPreviewUrl(cur) && getPreviewUrl(fresh)) updates.previewUrl = getPreviewUrl(fresh);
                if (Object.keys(updates).length) {
                    dispatch(updateNodeTrack({ id: nodeId, track: updates }));
                    changed = true;
                }
            }
            if (changed && wasClean) {
                const post = store.getState().whatsNext;
                savedSigRef.current = makeSetSignature(post.nodes, post.nodeOrder, post.setPath, post.activeNodeId);
            }
        } catch { /* cosmetic refresh — never surface */ }
    }, [dispatch, store]);

    const handleLoadSet = useCallback((set, from = 'modal') => {
        // A saved set keeps its step quota across reloads (keyed by its id).
        planIdRef.current = set.id || genSetId();
        setFreeQuota(null);
        setLockedPreview(null);
        // Restore nodeIdCounter to avoid collisions
        const maxId = set.nodeOrder.reduce((max, id) => {
            const num = parseInt(id.replace('wn-', ''), 10);
            return num > max ? num : max;
        }, 0);
        nodeIdCounter = maxId;

        dispatch(loadSavedState({
            nodes: set.nodes,
            nodeOrder: set.nodeOrder,
            setPath: set.setPath,
            activeNodeId: set.activeNodeId,
            panX: set.panX || 0,
            panY: set.panY || 0,
            zoom: set.zoom || 1,
        }));
        // Saved pan/zoom describe the viewport of the device that saved the
        // set — a set saved on desktop restores desktop-sized nodes on a
        // phone. Recompute a fit for THIS screen instead; the saved values
        // above only serve as a fallback if the fit can't be computed.
        // Double rAF: the canvas isn't mounted yet when loading from the
        // empty-planner start screen.
        const applyLoadFit = () => {
            const active = set.activeNodeId ? set.nodes[set.activeNodeId] : null;
            if (!active) { dispatch(setZoom(1)); dispatch(setPan({ x: 0, y: 0 })); return; }
            // Frame the WHOLE saved set, not just the active branch — the
            // branch-only bbox left every earlier track above the frame
            // (invisible) with dead space below on mobile.
            const others = (set.nodeOrder || [])
                .map(nid => set.nodes[nid])
                .filter(n => n && n.id !== set.activeNodeId);
            const fit = computeBranchFit(active, others);
            if (fit) {
                dispatch(setZoom(fit.zoom));
                dispatch(setPan({ x: fit.panX, y: fit.panY }));
            }
        };
        requestAnimationFrame(() => requestAnimationFrame(applyLoadFit));
        // Android WebView settles its layout AFTER the rAFs (sheet close /
        // late resize), so the first fit centers against a stale canvas
        // height — content ends up too high. One delayed re-fit corrects
        // it; where the layout was already stable it recomputes the same
        // values, so there's no visible jump.
        setTimeout(applyLoadFit, 450);
        // Remember which set we're now editing, and treat the loaded state as
        // the clean baseline so Back/Reset won't falsely warn.
        setCurrentSetId(set.id || null);
        setCurrentSetName(set.name || '');
        setCurrentSetCover(set.cover || null);
        setCurrentSetDescription(set.description || '');
        savedSigRef.current = makeSetSignature(set.nodes, set.nodeOrder, set.setPath, set.activeNodeId);
        setSaveStatus('saved');
        setShowSavedSets(false);
        trackEvent('planner_set_loaded', { track_count: (set.setPath || []).length, is_cloud: !!(isAuthenticated && accessToken), from });
        // Fire-and-forget: refresh covers/links by catalog id after render.
        hydrateSetFromCatalog(set);
    }, [dispatch, isAuthenticated, accessToken, hydrateSetFromCatalog, computeBranchFit]);

    const handleDeleteSavedSet = useCallback((set, e) => {
        e.stopPropagation();
        const updated = savedSets.filter(s => (s.id && set.id ? s.id !== set.id : s.name !== set.name));
        persistSavedSets(updated);
        setSavedSets(updated);
        trackEvent('planner_set_deleted', { track_count: (set.setPath || []).length, is_cloud: !!(set.id && isAuthenticated && accessToken) });
        // If we deleted the set we were editing, it's no longer a saved set.
        if ((set.id && currentSetId === set.id) || (!set.id && currentSetName === set.name)) {
            setCurrentSetId(null);
            setCurrentSetName('');
            setCurrentSetCover(null);
            setCurrentSetDescription('');
            setSaveStatus(null);
            savedSigRef.current = null;
        }
        // Best-effort cloud delete (a failure just means the set reappears on
        // the next session's list fetch).
        if (set.id && isAuthenticated && accessToken && window.electron?.library?.deletePlannerSet) {
            refreshAndRetry((token) => window.electron.library.deletePlannerSet(token, set.id)).catch(() => {});
        }
    }, [savedSets, currentSetId, currentSetName, isAuthenticated, accessToken]);

    // --- Export ---
    const handleExport = useCallback(() => {
        if (setPath.length === 0) return;
        const firstTrack = nodes[setPath[0]]?.track;
        const defaultName = `Set Planner - ${firstTrack?.title || 'Set'}`;
        setExportName(defaultName);
        setExportDjName((savedDjName || '').trim() || userRealName(authUser));
        setShowExport(true);
    }, [setPath, nodes, savedDjName, authUser]);

    const handleDjNameChange = useCallback((val) => {
        setExportDjName(val);
        dispatch(updateSettings({ djName: val }));
        try { localStorage.setItem('mixgoat-dj-name', val); } catch (_) { /* private mode */ }
    }, [dispatch]);

    const handleCopyPlaylist = useCallback(() => {
        const tracks = setPath.map(id => nodes[id]?.track).filter(Boolean);
        const text = tracks.map((t, i) => {
            const label = t.artist ? `${t.artist} - ${t.title}` : t.title;
            return `${i + 1}. ${label}`;
        }).join('\n');
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }, [setPath, nodes]);

    // PNG export — same branded card as Mixpedia's "Share image", with the
    // set's details (name, DJ, track count, estimated length, BPM range) and
    // the WHOLE tracklist (BPM · key per row). Two layouts:
    //   'pages'  — 9:16 story pages (page 1 = header + stats + first tracks,
    //              then compact continuation pages) so every page posts
    //              uncropped as an Instagram/TikTok story or carousel slide
    //   'single' — one tall poster with the whole list (WhatsApp/X/print)
    // Short sets fit one page either way, so they export straight away;
    // longer sets get asked which they want (handleExportImage below).
    // Electron → save dialog (pages saved as <name>_N.png); web / native →
    // share sheet or downloads (shareImages handles the routing).
    const runExportImage = useCallback(async (layout) => {
        if (exportingImage) return;
        const tracks = setPath.map(id => nodes[id]?.track).filter(Boolean);
        if (tracks.length === 0) return;
        setExportingImage(true);
        try {
            const setName = (currentSetName || exportName || '').trim() || 'My Set';
            const djName = (exportDjName || '').trim();
            const lengthSec = estimateSetSeconds(tracks);
            const lengthLabel = formatSetLength(lengthSec);
            const bpms = tracks.map(t => Number(t.bpm)).filter(b => Number.isFinite(b) && b > 0);
            const bpmRange = bpms.length
                ? (Math.min(...bpms) === Math.max(...bpms)
                    ? `${Math.round(bpms[0])}`
                    : `${Math.round(Math.min(...bpms))}–${Math.round(Math.max(...bpms))}`)
                : null;
            // Compact-banner card: the facts live in one header line, no
            // stat tiles (share-image 'set' layout).
            const metaParts = [`${tracks.length} track${tracks.length !== 1 ? 's' : ''}`];
            if (lengthLabel) metaParts.push(lengthLabel);
            if (bpmRange) metaParts.push(`${bpmRange} BPM`);
            const cover = getSetCoverArt({ cover: currentSetCover, nodes, setPath }) || '';
            const tracklist = tracks.map(t => {
                const bpm = Number(t.bpm);
                const parts = [];
                if (Number.isFinite(bpm) && bpm > 0) parts.push(String(Math.round(bpm)));
                if (t.key) parts.push(formatKeyDisplay(t.key, keyNotation));
                return {
                    label: t.artist ? `${t.artist} - ${t.title}` : (t.title || ''),
                    artwork: getArtwork(t) || filePathToArtUrl(t.filePath) || '',
                    meta: parts.join(' · '),
                };
            });
            const description = (currentSetDescription || '').trim();
            const cardData = {
                type: 'set',
                name: setName,
                byline: djName || undefined,
                artworkUrl: cover,
                metaLine1: metaParts.join('  ·  '),
                metaLine2: description || undefined,
                stats: [],
                tracklist,
                totalTracksCount: tracks.length,
            };
            const pages = layout === 'single'
                ? [await generateShareImage(cardData)].filter(Boolean)
                : await generateShareImagePages(cardData);
            if (!pages.length) throw new Error('no image');
            const safeName = setName.replace(/[^a-zA-Z0-9]/g, '_') || 'set';
            const { method, filePath } = await shareImages(pages, `${safeName}_tracklist.png`);
            const savedFolder = filePath ? filePath.split('/').slice(-2)[0] : null;
            const what = pages.length > 1 ? `${pages.length} tracklist images` : 'tracklist image';
            if (method === 'downloaded') toast.success(`"${setName}" ${what} saved to Downloads`);
            else if (method === 'saved') toast.success(`"${setName}" ${what} saved${savedFolder ? ` to ${savedFolder}` : ''}`);
            else if (method === 'shared') toast.success(`"${setName}" ${what} shared`);
            if (method !== 'cancelled') {
                trackEvent('share_image_created', { entity_type: 'set', method, track_count: tracks.length, page_count: pages.length, layout });
            }
        } catch (err) {
            console.error('[WhatsNext] Export image error:', err);
            toast.error('Could not export image');
        } finally {
            setExportingImage(false);
        }
    }, [exportingImage, setPath, nodes, currentSetName, exportName, exportDjName, currentSetCover, currentSetDescription, keyNotation, toast]);

    // What fits on page 1 under the header + stats, and per continuation
    // page (share-image "set" layout: 96px rows, 200px footer). At or under
    // the first the two layouts are the same image, so don't bother asking.
    // The page count shown on the button is an estimate (a 3-line title
    // costs a row) — the renderer decides for real.
    const PAGE1_TRACKS = 11;
    const CONT_PAGE_TRACKS = 14;
    const handleExportImage = useCallback(() => {
        const n = setPath.length;
        if (n <= PAGE1_TRACKS) { runExportImage('pages'); return; }
        const pageCount = 1 + Math.ceil((n - PAGE1_TRACKS) / CONT_PAGE_TRACKS);
        setConfirmAction({
            title: 'Export as image',
            message: `${n} tracks won't fit one story-sized card. Story pages give you ${pageCount} 9:16 images that post uncropped as an Instagram / TikTok carousel; one tall image keeps the whole tracklist in a single (scrollable) PNG for WhatsApp, X or print.`,
            confirmLabel: 'One tall image',
            onConfirm: () => { setConfirmAction(null); runExportImage('single'); },
            saveLabel: `Story pages (${pageCount})`,
            onSave: () => { setConfirmAction(null); runExportImage('pages'); },
        });
    }, [setPath.length, runExportImage]);

    const handleExportToPlaylist = useCallback(async () => {
        const tracks = setPath.map(id => nodes[id]?.track).filter(Boolean);
        if (tracks.length === 0) return;

        const playlistName = exportName.trim() || `Set Planner - ${tracks[0]?.title || 'Set'}`;

        // Web / iOS / Android: no local SQLite — playlists live in the cloud
        // library (user-library-service), same path AddToPlaylistModal uses.
        if (isWeb) {
            if (!accessToken || !window.electron?.library?.createPlaylist) {
                toast.error('Sign in to save playlists');
                return;
            }
            try {
                const created = await refreshAndRetry((token) => window.electron.library.createPlaylist(token, {
                    name: playlistName,
                    description: `Exported from Set Planner (${tracks.length} tracks)`,
                    cover_image: getSetCoverArt({ cover: currentSetCover, nodes, setPath }) || null,
                }));
                if (created?.error) throw new Error(typeof created.error === 'string' ? created.error : 'create failed');
                const playlist = created?.data || created;
                if (!playlist?.id) throw new Error('no playlist id');
                let added = 0;
                for (const t of tracks) {
                    const res = await refreshAndRetry((token) => window.electron.library.addTrackToPlaylist(token, playlist.id, {
                        artist: t.artist || '',
                        title: t.title || 'Unknown',
                        trackPublicId: t.publicId || t.public_id || entityId(t) || null,
                        artwork: getArtwork(t) || '',
                        preview_url: getPreviewUrl(t) || null,
                        youtube_id: t.youtubeId || null,
                        soundcloud_url: t.soundcloudUrl || null,
                        file_path: '',
                    }));
                    if (!res?.error) added++;
                }
                dispatch(triggerPlaylistRefresh());
                toast.success(`Playlist "${playlistName}" created with ${added} track${added !== 1 ? 's' : ''}`);
                trackEvent('playlist_created', { track_count: added, from: 'set_planner' });
                setShowExport(false);
            } catch (err) {
                console.error('[WhatsNext] Export to cloud playlist error:', err);
                toast.error('Failed to create playlist');
            }
            return;
        }

        try {
            const onlineTracks = tracks.map(t => ({
                title: t.title || 'Unknown',
                artist: t.artist || '',
                artwork: getArtwork(t) || '',
                previewUrl: getPreviewUrl(t) || null,
                youtubeId: t.youtubeId || null,
                soundcloudUrl: t.soundcloudUrl || null,
                bpm: t.bpm || null,
                key: t.key || null,
                genre: t.genre || null,
                genres: t.genres || null,
                styles: t.styles || null,
                releaseDate: t.releaseDate || null,
                label: t.label || null,
            }));

            const playlist = await window.electron.db.playlists.createFromOnlineTracks(
                playlistName,
                onlineTracks,
                `Exported from Set Planner (${onlineTracks.length} tracks)`
            );

            if (playlist?.id) {
                const allPlaylists = await window.electron.db.playlists.getAll(false);
                dispatch(setPlaylists(allPlaylists || []));
                dispatch(setSelectedPlaylist(playlist.id));
                dispatch(triggerPlaylistRefresh());

                toast.success(`Playlist "${playlistName}" created with ${tracks.length} tracks`);
                trackEvent('playlist_created', { track_count: tracks.length, from: 'set_planner' });
                setShowExport(false);
            }
        } catch (err) {
            console.error('[WhatsNext] Export to playlist error:', err);
            toast.error('Failed to create playlist');
        }
    }, [setPath, nodes, exportName, dispatch, toast, isWeb, accessToken, currentSetCover]);

    // Queue all set tracks in the player. Mirrors the feed's Play All:
    // resolve only the FIRST track upfront and hand the whole list to the
    // listContext — advanceListContext lazy-resolves each next track as the
    // queue advances (no up-front bulk resolve).
    const handleQueueAll = useCallback(async () => {
        const tracks = setPath.map(id => nodes[id]?.track).filter(Boolean);
        const playable = tracks.filter(t =>
            getPreviewUrl(t) || (t.youtubeId && t.youtubeId !== '-1') || t.soundcloudUrl
        );
        if (playable.length === 0) {
            toast.error('No playable tracks in set');
            return;
        }

        setQueueAllLoading(true);
        try {
            const ok = await playAllTracks(dispatch, playable);
            if (!ok) {
                toast.error('Unable to load first track');
                return;
            }
            dispatch(setAutoRadio(false));
        } finally {
            setQueueAllLoading(false);
        }
    }, [setPath, nodes, dispatch, toast]);

    // Build connections
    const connections = useMemo(() => {
        const lines = [];
        nodeOrder.forEach(id => {
            const node = nodes[id];
            if (!node || !node.parentId) return;
            const parent = nodes[node.parentId];
            if (!parent) return;
            lines.push({
                x1: parent.x + NODE_W / 2,
                y1: parent.y + NODE_W / 2,
                x2: node.x + NODE_W / 2,
                y2: node.y + NODE_W / 2,
                isRec: node.isRecommendation,
            });
        });
        return lines;
    }, [nodes, nodeOrder]);

    // Compute SVG viewBox bounds
    const svgBounds = useMemo(() => {
        if (connections.length === 0) return { minX: 0, minY: 0, maxX: 1000, maxY: 800 };
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        connections.forEach(c => {
            minX = Math.min(minX, c.x1, c.x2);
            minY = Math.min(minY, c.y1, c.y2);
            maxX = Math.max(maxX, c.x1, c.x2);
            maxY = Math.max(maxY, c.y1, c.y2);
        });
        return { minX: minX - 100, minY: minY - 200, maxX: maxX + 200, maxY: maxY + 200 };
    }, [connections]);

    // --- Shared modals (rendered from both the pre-start screen and the
    // canvas view, so they're defined once) ---

    const closeSaveDialog = () => {
        setShowSaveDialog(false);
        setEditSetTarget(null);
    };

    // Preview shown when no custom cover is staged: first track's artwork of
    // whatever the dialog is editing (a listed set or the live canvas).
    const saveDialogDefaultCover = showSaveDialog
        ? getSetCoverArt(editSetTarget ? { ...editSetTarget, cover: null } : { nodes, setPath })
        : null;

    const saveDialogModal = showSaveDialog && (
        <div className="export-playlist-modal" onClick={closeSaveDialog}>
            <div className="export-modal-content save-set-dialog" onClick={(e) => e.stopPropagation()}>
                <h3>{saveAsMode ? 'Save As' : (editSetTarget || currentSetId) ? 'Edit Set Details' : 'Save Set'}</h3>
                <div className="set-details-edit">
                    <div
                        className={`set-cover-drop ${saveCoverDragOver ? 'dragging-over' : ''}`}
                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setSaveCoverDragOver(true); }}
                        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setSaveCoverDragOver(false); }}
                        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setSaveCoverDragOver(false); processCoverFile(e.dataTransfer.files?.[0]); }}
                    >
                        <label className="set-cover-click" title="Click or drag an image to set a custom cover">
                            <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={(e) => { processCoverFile(e.target.files?.[0]); e.target.value = ''; }}
                            />
                            {(saveCover || saveDialogDefaultCover) ? (
                                <img className={saveCover ? '' : 'default-cover'} src={saveCover || saveDialogDefaultCover} alt="Set cover" />
                            ) : (
                                <span className="set-cover-placeholder">&#9835;</span>
                            )}
                            <span className="set-cover-overlay">{saveCover ? 'Change' : 'Add cover'}</span>
                        </label>
                        {saveCover && (
                            <button
                                type="button"
                                className="set-cover-remove"
                                title="Remove custom cover"
                                onClick={() => setSaveCover(null)}
                            >&#215;</button>
                        )}
                    </div>
                    <div className="set-details-fields">
                        <input
                            type="text"
                            placeholder="Name your set..."
                            value={saveName}
                            onChange={(e) => setSaveName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveSet(saveAsMode); }}
                            autoFocus
                        />
                        <textarea
                            rows={3}
                            placeholder="Description (optional)"
                            value={saveDescription}
                            onChange={(e) => setSaveDescription(e.target.value)}
                        />
                        <span className="set-cover-note">
                            {saveCover ? 'Custom cover' : "No custom cover — the first track's artwork is used"}
                        </span>
                    </div>
                </div>
                <div className="export-actions">
                    <button onClick={closeSaveDialog}>Cancel</button>
                    {currentSetId && !saveAsMode && !editSetTarget && (
                        <button
                            onClick={() => { setSaveAsMode(true); setSaveName(`${currentSetName} copy`); }}
                            title="Save this set under a new name and keep working on it (⌘⇧S)"
                        >
                            Save As…
                        </button>
                    )}
                    <button className="export-copy-btn" onClick={() => handleSaveSet(saveAsMode)} disabled={!saveName.trim()}>
                        {saveAsMode ? 'Save As' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );

    const savedSetsModal = showSavedSets && (
        <div className="export-playlist-modal" onClick={() => setShowSavedSets(false)}>
            <div className="export-modal-content saved-sets-modal" onClick={(e) => e.stopPropagation()}>
                <div className="saved-sets-header">
                    <h3>Saved Sets</h3>
                    <span className="saved-sets-count">
                        {savedSetsQuery.trim()
                            ? `${filteredSavedSets.length} of ${savedSets.length}`
                            : savedSets.length}
                    </span>
                </div>
                <div className="saved-sets-search">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <input
                        type="text"
                        placeholder="Search by set name, artist or track..."
                        value={savedSetsQuery}
                        onChange={(e) => setSavedSetsQuery(e.target.value)}
                        onKeyDown={(e) => {
                            // Esc clears the query first; a second Esc (empty
                            // query) closes the modal.
                            if (e.key === 'Escape') {
                                e.stopPropagation();
                                if (savedSetsQuery) setSavedSetsQuery('');
                                else setShowSavedSets(false);
                            }
                        }}
                        // Desktop only: on phones auto-focus summons the
                        // keyboard the instant the sheet opens, which
                        // squeezes the list and hides the field being typed
                        // into. Users tap the field when they want to search.
                        autoFocus={!checkIsMobileWeb()}
                    />
                    {savedSetsQuery && (
                        <button className="saved-sets-search-clear" onClick={() => setSavedSetsQuery('')} title="Clear search">&#215;</button>
                    )}
                </div>
                <div className="export-tracks-list saved-sets-list">
                    {filteredSavedSets.length === 0 && (
                        <div className="saved-sets-empty">
                            {savedSets.length === 0 ? 'No saved sets yet' : 'No sets match your search'}
                        </div>
                    )}
                    {filteredSavedSets.map(({ set, matchType, matchLabel }) => {
                        const trackCount = set.setPath?.length || 0;
                        const coverArt = getSetCoverArt(set);
                        return (
                            <div
                                key={set.id || set.name}
                                className={`saved-set-item${(set.id && currentSetId === set.id) ? ' saved-set-item--current' : ''}`}
                                onClick={() => handleLoadSet(set)}
                            >
                                <div className="saved-set-cover">
                                    {coverArt ? (
                                        <img src={coverArt} alt="" />
                                    ) : (
                                        <span>&#9835;</span>
                                    )}
                                </div>
                                <div className="saved-set-info">
                                    <span className="saved-set-name">{highlightMatch(set.name, savedSetsQuery)}</span>
                                    {set.description && (
                                        <span className="saved-set-desc">{highlightMatch(set.description, savedSetsQuery)}</span>
                                    )}
                                    <span className="saved-set-meta">
                                        {trackCount} track{trackCount !== 1 ? 's' : ''}
                                        {trackCount > 0 && <> · <SetLength seconds={estimateSavedSetSeconds(set)} /></>}
                                        {matchType === 'track' && (
                                            // The hit lives in a track the row doesn't
                                            // otherwise show — surface the evidence.
                                            <span className="saved-set-match"> · &#9835; {highlightMatch(matchLabel, savedSetsQuery)}</span>
                                        )}
                                        {set.date ? ` · ${formatSetDate(set.date)}` : ''}
                                    </span>
                                </div>
                                <button
                                    className="saved-set-edit"
                                    onClick={(e) => handleEditSetDetails(set, e)}
                                    title="Edit details"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
                                </button>
                                <button
                                    className="saved-set-delete"
                                    onClick={(e) => handleDeleteSavedSet(set, e)}
                                    title="Delete"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                </button>
                            </div>
                        );
                    })}
                </div>
                <div className="export-actions">
                    <button onClick={() => setShowSavedSets(false)}>Close</button>
                </div>
            </div>
        </div>
    );

    if (!hasStarted) {
        return (
            <div className="whats-next-tab">
                <div className="whats-next-start">
                    <div className="start-title">Set Planner</div>
                    <div className="start-subtitle">
                        Pick a track to start your set. MixGoat suggests what to mix next from what DJs actually played after it.
                    </div>
                    {connectFailed && (
                        <div className="connect-banner">
                            <span>Connection failed: {tokenError}</span>
                            <button className="connect-banner-retry" onClick={retryConnect}>Retry</button>
                        </div>
                    )}
                    {!canAccess && (
                        <div className="free-plan-banner">
                            Free plan: {FREE_STEP_LIMIT} recommendation steps per set, {FREE_SET_LIMIT} saved sets.{' '}
                            <span className="free-plan-upgrade" onClick={requireUpgrade}>Upgrade to GOAT</span> for unlimited steps, sets, and export.
                        </div>
                    )}
                    <div className="start-search">
                        <DJPalSearchBar
                            placeholder="Search a track to begin..."
                            value={searchQuery}
                            onChange={setSearchQuery}
                            onSearch={() => handleStart()}
                            onClear={() => setSearchQuery('')}
                            disabled={isLoading}
                            fetchSuggestions={fetchTrackSuggestions}
                            onSuggestionSelect={handleSuggestionSelect}
                            recentEntries={recentSearches}
                            recentLabel="Recent searches"
                            onRecentSelect={handleRecentSelect}
                            onRecentDismiss={handleRecentDismiss}
                            onRecentClear={handleRecentClear}
                        />
                        <button
                            className="start-btn"
                            onClick={() => handleStart()}
                            disabled={!searchQuery.trim() || isLoading}
                        >
                            {isLoading ? 'Loading...' : v2 ? "Let's go" : "Let's Go"}
                        </button>
                    </div>
                    {(recentSets.length > 0 || showRecentSkeleton) && (
                        <div className="start-recent-sets">
                            <div className="start-recent-header">
                                <div className="start-recent-title">{v2 && v2AllSets ? `All sets · ${recentSets.length}` : 'Recent'}</div>
                                {/* The searchable Saved Sets modal — the single "view all".
                                    Under the skeleton it goes invisible rather than away:
                                    its count is the thing not known yet, but the button is
                                    6px taller than the title and the header would resize
                                    under the row when the count landed. */}
                                <button
                                    className={`start-recent-browse${showRecentSkeleton ? ' start-recent-browse--ghost' : ''}`}
                                    onClick={showRecentSkeleton ? undefined : (v2 ? () => setV2AllSets((a) => !a) : openSavedSets)}
                                    title={showRecentSkeleton ? undefined : 'Browse and search saved sets'}
                                    aria-hidden={showRecentSkeleton || undefined}
                                    tabIndex={showRecentSkeleton ? -1 : undefined}
                                >
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
                                    {v2 && v2AllSets ? 'Show less' : `Browse all (${recentSets.length})`}
                                </button>
                            </div>
                            <div ref={recentGridRef} className="start-recent-grid" aria-busy={showRecentSkeleton || undefined}>
                                {/* Mutually exclusive with the map below: a skeleton only
                                    shows when there is no set to render. */}
                                {showRecentSkeleton && Array.from({ length: recentSkeletonCount }, (_, i) => (
                                    <div key={`skeleton-${i}`} className="start-recent-card start-recent-card--skeleton" aria-hidden="true">
                                        <div className="start-recent-cover" />
                                        <div className="start-recent-name"><span className="start-recent-skel-bar">{'\u00A0'}</span></div>
                                        <div className="start-recent-meta">
                                            <span className="start-recent-meta-line"><span className="start-recent-skel-bar start-recent-skel-bar--mid">{'\u00A0'}</span></span>
                                            <span className="start-recent-meta-line"><span className="start-recent-skel-bar start-recent-skel-bar--short">{'\u00A0'}</span></span>
                                        </div>
                                    </div>
                                ))}
                                {visibleRecentSets.map((set) => {
                                    const trackCount = set.setPath?.length || 0;
                                    const arts = getSetCoverMosaic(set);
                                    return (
                                        <button
                                            key={set.id || set.name}
                                            className="start-recent-card"
                                            onClick={() => handleLoadSet(set, 'recent')}
                                            title={set.name}
                                        >
                                            <div className={`start-recent-cover${arts.length >= 2 ? ` start-recent-cover--mosaic start-recent-cover--n${arts.length}` : ''}`}>
                                                {arts.length === 0
                                                    ? <span>&#9835;</span>
                                                    : arts.map((src, i) => <img key={i} src={src} alt="" />)}
                                            </div>
                                            <div className="start-recent-name">{set.name}</div>
                                            <div className="start-recent-meta">
                                                <span className="start-recent-meta-line">
                                                    {trackCount} track{trackCount !== 1 ? 's' : ''}
                                                    {trackCount > 0 && <> · <SetLength seconds={estimateSavedSetSeconds(set)} /></>}
                                                </span>
                                                {set.date && <span className="start-recent-meta-line start-recent-date">{formatSetDate(set.date)}</span>}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {error && <div style={{ color: 'var(--color-danger)', fontSize: 13, marginTop: 8 }}>{error}</div>}
                </div>

                {/* Saved Sets Modal + details dialog (row edit) */}
                {savedSetsModal}
                {saveDialogModal}
            </div>
        );
    }

    const hasRecommendations = nodeOrder.some(id => nodes[id]?.isRecommendation);

    // ── Add-track bodies (shared by the desktop dropdowns and the phone sheet) ──
    const isMobileWeb = checkIsMobileWeb();
    const renderLibraryAddBody = () => {
                        const q = addTrackQuery.trim();
                        const searching = q.length >= MIN_SEARCH_CHARS;
                        const artOf = (t) => t.artworkUrl || t.artwork || t.cover || t.track?.artwork || t.track?.cover || null;
                        const nameOf = (t) => {
                            const artist = t.track?.artist || t.artist || '';
                            const title = getTrackTitle(t);
                            return artist ? `${artist} - ${title}` : title;
                        };
                        const trackRow = (t, key, onClick, meta) => (
                            <button key={key} className="add-track-item" onClick={onClick}>
                                {artOf(t)
                                    ? <img className="add-track-item-art" src={artOf(t)} alt="" />
                                    : <span className="add-track-item-art add-track-item-art-placeholder">&#9835;</span>}
                                <span className="add-track-item-text">
                                    <span className="add-track-item-name">{nameOf(t)}</span>
                                    {meta && <span className="add-track-item-meta">{meta}</span>}
                                </span>
                            </button>
                        );
        return (
            <>
                <div className="add-track-search">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <input
                        ref={addTrackInputRef}
                        type="text"
                        placeholder={isWeb ? 'Search your playlists...' : 'Search library & playlists...'}
                        value={addTrackQuery}
                        onChange={(e) => setAddTrackQuery(e.target.value)}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter' && addTrackQuery.trim()) {
                                if (addTrackResults[0]) handleAddLibraryTrack(addTrackResults[0]);
                                else if (addTrackPlaylistMatches[0]) handleAddLibraryTrack(addTrackPlaylistMatches[0].track);
                                else handleAddTrack();
                            }
                            if (e.key === 'Escape') { setShowAddTrack(false); setAddTrackQuery(''); }
                        }}
                        autoFocus={!checkIsMobileWeb()}
                    />
                    {addTrackQuery && (
                        <button type="button" className="add-track-clear" onClick={() => setAddTrackQuery('')} aria-label="Clear">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                    )}
                </div>
                <div className="add-track-results">
                    {!q && libraryRecTracks.length > 0 && (
                        <div className="add-track-section add-track-recommended">
                            <div className="add-track-section-title">Recommended from your library</div>
                            {libraryRecTracks.map((rec, i) => (
                                <button key={`rec-${i}`} className="add-track-item add-track-rec-match" onClick={() => handleAddLibraryTrack({ track: { publicId: entityId(rec), artist: rec.artist, title: rec.title, cover: getArtwork(rec) } })}>
                                    <span className="add-track-item-name">{rec.artist} - {rec.title}</span>
                                    <span className="add-track-item-badge">{v2 ? '' : '♫ '}Fits your set</span>
                                </button>
                            ))}
                        </div>
                    )}
                    {!q && addTrackPlaylists.length > 0 && (
                        <div className="add-track-section">
                            <div className="add-track-section-title">Playlists</div>
                            {addTrackPlaylists.map(pl => (
                                <div key={pl.id}>
                                    <button
                                        className={`add-track-item add-track-playlist${expandedPlaylistId === pl.id ? ' expanded' : ''}`}
                                        onClick={() => setExpandedPlaylistId(expandedPlaylistId === pl.id ? null : pl.id)}
                                    >
                                        <span className="add-track-item-name">{pl.name}</span>
                                        <span className="add-track-item-count">{pl.trackCount ?? pl.tracks?.length ?? 0}</span>
                                    </button>
                                    {expandedPlaylistId === pl.id && expandedPlaylistTracks.map((t, i) => (
                                        <button key={i} className="add-track-item add-track-sub" onClick={() => handleAddLibraryTrack(t)}>
                                            <span className="add-track-item-name">{nameOf(t)}</span>
                                        </button>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                    {isWeb && !q && addTrackPlaylists.length === 0 && (
                        <div className="add-track-empty">
                            No cloud playlists yet. Get the desktop app to import your library.
                        </div>
                    )}
                    {q && !searching && (
                        <div className="add-track-empty">Type {MIN_SEARCH_CHARS}+ characters to search</div>
                    )}
                    {searching && addTrackResults.length > 0 && (
                        <div className="add-track-section">
                            <div className="add-track-section-title">Library matches</div>
                            {addTrackResults.map((t, i) => trackRow(t, `lib-${i}`, () => handleAddLibraryTrack(t)))}
                        </div>
                    )}
                    {searching && addTrackPlaylistMatches.length > 0 && (
                        <div className="add-track-section">
                            <div className="add-track-section-title">In your playlists</div>
                            {addTrackPlaylistMatches.map(({ track: t, playlistNames }, i) => trackRow(
                                t, `pl-${i}`, () => handleAddLibraryTrack(t),
                                playlistNames.length > 1 ? `${playlistNames[0]} +${playlistNames.length - 1}` : playlistNames[0]
                            ))}
                        </div>
                    )}
                    {searching && addTrackResults.length === 0 && addTrackPlaylistMatches.length === 0 && (
                        <button className="add-track-item add-track-manual" onClick={handleAddTrack}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                            Add "{q}"
                        </button>
                    )}
                </div>
            </>
        );
    };
    const renderMixpediaAddBody = () => (
        <>
            <div className="add-track-search">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input
                    ref={addMixpediaInputRef}
                    type="text"
                    placeholder="Search a track..."
                    value={addMixpediaQuery}
                    onChange={(e) => setAddMixpediaQuery(e.target.value)}
                    onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Escape') { setShowAddMixpedia(false); setAddMixpediaQuery(''); setAddMixpediaResults([]); }
                    }}
                    autoFocus={!checkIsMobileWeb()}
                />
            </div>
            <div className="add-track-results">
                {addMixpediaQuery.trim().length >= MIN_SEARCH_CHARS && addMixpediaLoading && addMixpediaResults.length === 0 && (
                    <div className="add-track-empty">Searching…</div>
                )}
                {addMixpediaResults.length > 0 && (
                    <div className="add-track-section">
                        {addMixpediaResults.map((sug, i) => (
                            <button
                                key={entityId(sug) || `${sug.rawArtist}-${sug.rawTitle}-${i}`}
                                className="add-track-item"
                                onClick={() => handleAddMixpediaTrack(sug)}
                            >
                                {sug.artworkUrl
                                    ? <img className="add-track-item-art" src={safeArtwork(sug.artworkUrl)} alt="" />
                                    : <span className="add-track-item-art add-track-item-art-placeholder">&#9835;</span>
                                }
                                <span className="add-track-item-name">
                                    {sug.rawArtist ? `${sug.rawArtist} - ` : ''}{sug.rawTitle || sug.title}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
                {addMixpediaQuery.trim().length >= MIN_SEARCH_CHARS && !addMixpediaLoading && addMixpediaResults.length === 0 && (
                    <div className="add-track-empty">No matches</div>
                )}
                {addMixpediaQuery.trim().length < MIN_SEARCH_CHARS && (
                    <div className="add-track-empty">Type {MIN_SEARCH_CHARS}+ characters to search</div>
                )}
            </div>
        </>
    );
    const openAddSheet = () => {
        setAddSheetTab('mixpedia');
        setShowAddTrack(true);
        setShowAddMixpedia(true); // Mixpedia tab is the default → its autocomplete effect must be armed
        setAddTrackQuery('');
        setAddMixpediaQuery('');
        setAddMixpediaResults([]);
        setExpandedPlaylistId(null);
    };
    const closeAddSheet = () => {
        setShowAddTrack(false);
        setShowAddMixpedia(false);
        setAddTrackQuery('');
        setAddMixpediaQuery('');
        setAddMixpediaResults([]);
        setExpandedPlaylistId(null);
    };
    const switchAddSheetTab = (tab) => {
        setAddSheetTab(tab);
        // The Mixpedia autocomplete effect is keyed on showAddMixpedia.
        setShowAddMixpedia(tab === 'mixpedia');
    };

    return (<>
        <div className="whats-next-tab">
            {localFallbackNotice && (
                <div className="wn-fallback-notice">
                    <svg className="wn-fallback-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="9" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span className="wn-fallback-text">{localFallbackNotice}</span>
                    <button className="wn-fallback-dismiss" onClick={() => setLocalFallbackNotice(null)} title="Dismiss">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                </div>
            )}
            <AddToPlaylistModal
                show={!!playlistModalTrack}
                onHide={() => setPlaylistModalTrack(null)}
                track={playlistModalTrack || {}}
            />
            {/* Toolbar */}
            {v2 ? (
            <div className="whats-next-toolbar v2-planner-toolbar">
                <div className="v2-planner-left">
                    <button type="button" className="v2-btn quiet icon" onClick={handleReset} title="Back to start">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
                    </button>
                    <div className="v2-planner-title">
                        {currentSetId ? (
                            <div className="toolbar-set-name-wrap">
                                <button type="button" className="v2-planner-name" onClick={() => setShowSetMenu(v => !v)} title="Set options">
                                    <span className="set-name-text">{currentSetName}</span>
                                    <svg className="set-name-chevron" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                                </button>
                                {showSetMenu && (
                                    <>
                                        <div className="set-name-menu-overlay" onClick={() => setShowSetMenu(false)} />
                                        <div className="set-name-menu">
                                            <button onClick={() => { setShowSetMenu(false); setSaveName(currentSetName); setSaveCover(currentSetCover); setSaveDescription(currentSetDescription); setEditSetTarget(null); setSaveAsMode(false); setShowSaveDialog(true); }}>
                                                Edit Details…
                                            </button>
                                            <button onClick={() => { setShowSetMenu(false); handleSaveAsClick(); }}>
                                                Save As…
                                                <span className="menu-shortcut">{navigator.platform.toUpperCase().includes('MAC') ? '⌘⇧S' : 'Ctrl+Shift+S'}</span>
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="v2-planner-name static">New set</div>
                        )}
                        <div className="v2-planner-sub v2-num">
                            {setPath.length} track{setPath.length !== 1 ? 's' : ''}
                            {setPath.length > 0 && <> · <SetLength seconds={estimateSetSeconds(setPath.map(id => nodes[id]?.track).filter(Boolean))} /></>}
                            {setPath.length > 0 && (
                                hasUnsavedChanges
                                    ? <> · <span className="v2-planner-dirty">unsaved</span></>
                                    : saveStatus === 'offline' ? ' · offline' : saveStatus === 'saving' ? ' · saving…' : (currentSetId ? ' · saved' : '')
                            )}
                            {!canAccess && !lockedPreview && freeQuota && freeQuota.used < freeQuota.limit && <span className="set-limit-hint"> · {freeQuota.limit - freeQuota.used} free step{freeQuota.limit - freeQuota.used === 1 ? '' : 's'} left</span>}
                            {!canAccess && (lockedPreview || (freeQuota && freeQuota.used >= freeQuota.limit)) && <span className="set-limit-hint"> · free steps used</span>}
                        </div>
                    </div>
                </div>
                <div className="v2-planner-mid">
                    {isMobileWeb ? (
                        <button type="button" className="v2-btn primary" onClick={openAddSheet} title="Add a track">+ Add track</button>
                    ) : (
                        <div className="toolbar-add-track-wrap v2-planner-add" ref={addTrackRef}>
                            <div ref={v2AddRef} className="v2-planner-add-inner">
                                <button
                                    type="button"
                                    className="v2-btn primary"
                                    onClick={() => { setV2AddMenu(v => (v ? null : 'menu')); setShowAddTrack(false); setShowAddMixpedia(false); }}
                                    aria-expanded={!!v2AddMenu}
                                >
                                    + Add track
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 12, height: 12 }}><polyline points="6 9 12 15 18 9" /></svg>
                                </button>
                                {v2AddMenu === 'menu' && (
                                    <div className="v2-popover v2-planner-menu" role="menu">
                                        <button type="button" role="menuitem" onClick={() => { setV2AddMenu(null); setShowAddMixpedia(true); setAddMixpediaQuery(''); setAddMixpediaResults([]); }}>
                                            Search Mixpedia
                                            <span className="v2-popover-hint">Tracks DJs play, whether or not you own them</span>
                                        </button>
                                        <button type="button" role="menuitem" onClick={() => { setV2AddMenu(null); setShowAddTrack(true); setAddTrackQuery(''); setExpandedPlaylistId(null); }}>
                                            From your library
                                            <span className="v2-popover-hint">Your tracks and playlists</span>
                                        </button>
                                        <button type="button" role="menuitem" onClick={() => { setV2AddMenu('new'); }}>
                                            Start a new set from a track
                                            <span className="v2-popover-hint">Replaces the current set</span>
                                        </button>
                                    </div>
                                )}
                                {v2AddMenu === 'new' && (
                                    <div className="v2-popover v2-planner-menu v2-planner-new">
                                        <div className="v2-popover-title">Start a new set from a track</div>
                                        <div className="toolbar-search">
                                            <DJPalSearchBar
                                                placeholder="Start a new set from a track..."
                                                value={searchQuery}
                                                onChange={setSearchQuery}
                                                onSearch={() => { setV2AddMenu(null); handleStart(); }}
                                                onClear={() => setSearchQuery('')}
                                                disabled={isLoading}
                                                fetchSuggestions={fetchTrackSuggestions}
                                                onSuggestionSelect={(sug) => { setV2AddMenu(null); handleSuggestionSelect(sug); }}
                                                recentEntries={recentSearches}
                                                recentLabel="Recent searches"
                                                onRecentSelect={(entry) => { setV2AddMenu(null); handleRecentSelect(entry); }}
                                                onRecentDismiss={handleRecentDismiss}
                                                onRecentClear={handleRecentClear}
                                            />
                                            <button type="button" className="v2-btn sm" onClick={() => { setV2AddMenu(null); handleStart(); }} disabled={!searchQuery.trim() || isLoading}>
                                                New set
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {showAddTrack && (
                                <div className="add-track-dropdown">
                                    {renderLibraryAddBody()}
                                </div>
                            )}
                            <div ref={addMixpediaRef} className="v2-planner-mixpedia-anchor">
                                {showAddMixpedia && (
                                    <div className="add-track-dropdown">
                                        {renderMixpediaAddBody()}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    <button type="button" className="v2-btn quiet icon" onClick={handleUndo} disabled={isLoading || setPath.length <= 1} title="Undo last track">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 00-4-4H4"/></svg>
                    </button>
                </div>
                <div className="v2-planner-right">
                    <button type="button" className="v2-btn" onClick={handleQueueAll} disabled={queueAllLoading || setPath.length <= 1} title="Queue the whole set in the player">
                        {queueAllLoading
                            ? <span className="mix-play-spinner" />
                            : <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21 6 3"/></svg>}
                        Play set
                    </button>
                    <button
                        type="button"
                        className={`v2-btn${canAccess ? '' : ' locked'}`}
                        onClick={canAccess ? handleExport : requireUpgrade}
                        disabled={setPath.length <= 1}
                        title={canAccess ? 'Share or export the set (copy, PNG, playlist)' : 'Export is a GOAT feature'}
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v7a2 2 0 002 2h12a2 2 0 002-2v-7"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
                        Share
                    </button>
                    <div className="v2-planner-more" ref={v2MoreRef}>
                        <button type="button" className={`v2-btn quiet icon${v2MoreMenu ? ' on' : ''}`} onClick={() => setV2MoreMenu(v => !v)} title="More" aria-expanded={v2MoreMenu}>
                            <svg viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" /></svg>
                        </button>
                        {v2MoreMenu && (
                            <div className="v2-popover v2-planner-menu" role="menu">
                                {savedSets.length > 0 && (
                                    <button type="button" role="menuitem" onClick={() => { setV2MoreMenu(false); openSavedSets(); }}>Open saved set…</button>
                                )}
                                <button type="button" role="menuitem" onClick={() => { setV2MoreMenu(false); handleSaveClick(); }}>
                                    Save
                                    <span className="menu-shortcut">{navigator.platform.toUpperCase().includes('MAC') ? '⌘S' : 'Ctrl+S'}</span>
                                </button>
                                <button type="button" role="menuitem" onClick={() => { setV2MoreMenu(false); handleSaveAsClick(); }}>Save as…</button>
                                <button type="button" role="menuitem" disabled={isLoading} onClick={() => { setV2MoreMenu(false); handleRefreshRecs(); }}>Refresh picks</button>
                                <button type="button" role="menuitem" disabled={setPath.length <= 1} onClick={() => { setV2MoreMenu(false); if (canAccess) handleExport(); else requireUpgrade(); }}>Export…</button>
                                <button type="button" role="menuitem" onClick={() => { setV2MoreMenu(false); handleReset(); }}>Reset</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            ) : (
            <div className="whats-next-toolbar">
                <button
                    className="toolbar-btn toolbar-back-btn"
                    onClick={handleReset}
                    title="Back to start"
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12" />
                        <polyline points="12 19 5 12 12 5" />
                    </svg>
                </button>
                <div className="toolbar-search">
                    <DJPalSearchBar
                        placeholder={isMobileWeb ? 'New set from a track…' : 'Start a new set from a track...'}
                        value={searchQuery}
                        onChange={setSearchQuery}
                        onSearch={() => handleStart()}
                        onClear={() => setSearchQuery('')}
                        disabled={isLoading}
                        fetchSuggestions={fetchTrackSuggestions}
                        onSuggestionSelect={handleSuggestionSelect}
                        recentEntries={recentSearches}
                        recentLabel="Recent searches"
                        onRecentSelect={handleRecentSelect}
                        onRecentDismiss={handleRecentDismiss}
                        onRecentClear={handleRecentClear}
                    />
                    <button
                        className="toolbar-new-set-btn"
                        onClick={() => handleStart()}
                        disabled={!searchQuery.trim() || isLoading}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        <span className="toolbar-new-set-label">New Set</span>
                    </button>
                </div>
                <div className="toolbar-actions">
                    <span className="set-count">
                        {setPath.length} track{setPath.length !== 1 ? 's' : ''}<span className="set-count-suffix"> in set</span>
                        {setPath.length > 0 && <> · <SetLength className="set-length--toolbar" seconds={estimateSetSeconds(setPath.map(id => nodes[id]?.track).filter(Boolean))} /></>}
                        {!canAccess && !lockedPreview && freeQuota && freeQuota.used < freeQuota.limit && <span className="set-limit-hint"> · {freeQuota.limit - freeQuota.used} free step{freeQuota.limit - freeQuota.used === 1 ? '' : 's'} left</span>}
                        {!canAccess && (lockedPreview || (freeQuota && freeQuota.used >= freeQuota.limit)) && <span className="set-limit-hint"> · free steps used</span>}
                    </span>
                    {hasStarted && !isMobileWeb && (
                        <div className="toolbar-add-track-wrap" ref={addTrackRef}>
                            <button
                                className="toolbar-btn toolbar-add-btn"
                                onClick={() => { setShowAddTrack(!showAddTrack); setAddTrackQuery(''); setExpandedPlaylistId(null); }}
                                title="Add track from your library or playlists"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                <span>From Library</span>
                            </button>
                            {showAddTrack && (
                                <div className="add-track-dropdown">
                                    {renderLibraryAddBody()}
                                </div>
                            )}
                        </div>
                    )}
                    {hasStarted && !isMobileWeb && (
                        <div className="toolbar-add-track-wrap" ref={addMixpediaRef}>
                            <button
                                className="toolbar-btn toolbar-add-btn"
                                onClick={() => { setShowAddMixpedia(!showAddMixpedia); setAddMixpediaQuery(''); setAddMixpediaResults([]); }}
                                title="Search Mixpedia and add a track"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                                <span>From Mixpedia</span>
                            </button>
                            {showAddMixpedia && (
                                <div className="add-track-dropdown">
                                    {renderMixpediaAddBody()}
                                </div>
                            )}
                        </div>
                    )}
                    {/* Phone: ONE "+" → a bottom sheet with two tabs (Mixpedia |
                        Your playlists). Icon-only buttons couldn't tell the two sources
                        apart, and a dropdown under a 40px toolbar button doesn't
                        work on a phone. Desktop keeps the two labelled dropdowns. */}
                    {hasStarted && isMobileWeb && (
                        <div className="toolbar-add-track-wrap">
                            <button
                                className="toolbar-btn toolbar-add-btn"
                                onClick={openAddSheet}
                                title="Add a track"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                <span>Add Track</span>
                            </button>
                        </div>
                    )}
                    {currentSetId && (
                        <div className="toolbar-set-name-wrap">
                            <button
                                className="toolbar-set-name"
                                onClick={() => setShowSetMenu(v => !v)}
                                title="Set options"
                            >
                                <span className="set-name-text">{currentSetName}</span>
                                {hasUnsavedChanges && <span className="set-dirty-dot" title="Unsaved changes">•</span>}
                                {!hasUnsavedChanges && saveStatus === 'offline' && <span className="set-save-status offline">offline</span>}
                                <svg className="set-name-chevron" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                            </button>
                            {showSetMenu && (
                                <>
                                    <div className="set-name-menu-overlay" onClick={() => setShowSetMenu(false)} />
                                    <div className="set-name-menu">
                                        <button onClick={() => { setShowSetMenu(false); setSaveName(currentSetName); setSaveCover(currentSetCover); setSaveDescription(currentSetDescription); setEditSetTarget(null); setSaveAsMode(false); setShowSaveDialog(true); }}>
                                            Edit Details…
                                        </button>
                                        <button onClick={() => { setShowSetMenu(false); handleSaveAsClick(); }}>
                                            Save As…
                                            <span className="menu-shortcut">{navigator.platform.toUpperCase().includes('MAC') ? '⌘⇧S' : 'Ctrl+Shift+S'}</span>
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                    {hasStarted && (
                        <button className="toolbar-btn save-btn" onClick={handleSaveClick} title="Save set (⌘S)">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                        </button>
                    )}
                    {savedSets.length > 0 && (
                        <button className="toolbar-btn" onClick={openSavedSets} title="Open saved set">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
                        </button>
                    )}
                    {setPath.length > 1 && (
                        <button className="toolbar-btn" onClick={handleUndo} disabled={isLoading} title="Undo last track">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 00-4-4H4"/></svg>
                        </button>
                    )}
                    {setPath.length > 1 && (
                        <button className="toolbar-btn" onClick={handleQueueAll} disabled={queueAllLoading} title="Queue all tracks in player">
                            {queueAllLoading
                                ? <span className="mix-play-spinner" />
                                : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/><line x1="23" y1="5" x2="23" y2="19"/></svg>}
                        </button>
                    )}
                    {setPath.length > 1 && (
                        <button className={`toolbar-btn export-btn${canAccess ? '' : ' locked'}`} onClick={canAccess ? handleExport : requireUpgrade} title={canAccess ? 'Export set (copy, PNG, playlist)' : 'Export is a GOAT feature'}>
                            {/* Share/export glyph (arrow OUT of the tray) — same as Mixpedia's
                                Share; the old download arrow implied "save a file". */}
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v7a2 2 0 002 2h12a2 2 0 002-2v-7"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
                        </button>
                    )}
                    <button className="toolbar-btn toolbar-reset-btn" onClick={handleReset} title="Reset">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 019-9 9.75 9.75 0 016.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 01-9 9 9.75 9.75 0 01-6.74-2.74L3 16"/><path d="M8 16H3v5"/></svg>
                    </button>
                </div>
            </div>
            )}

            {/* Canvas */}
            <div
                className={`whats-next-canvas ${isPanning ? 'dragging' : ''} ${draggingNodeId ? 'dragging-node' : ''}`}
                ref={canvasRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onWheel={handleWheel}
            >
                <div
                    className="canvas-inner"
                    ref={innerRef}
                    style={{ transform: `translate(${panX}px, ${panY}px) scale(${zoom})` }}
                >
                    {/* SVG Connections */}
                    <svg
                        className="connections-svg"
                        width={svgBounds.maxX - svgBounds.minX}
                        height={svgBounds.maxY - svgBounds.minY}
                        style={{
                            left: svgBounds.minX,
                            top: svgBounds.minY,
                        }}
                    >
                        <defs>
                            <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="var(--color-accent)" stopOpacity="0.8" />
                                <stop offset="100%" stopColor="var(--color-accent)" stopOpacity="0.3" />
                            </linearGradient>
                        </defs>
                        {connections.map((c, i) => {
                            const cx1 = c.x1 - svgBounds.minX;
                            const cy1 = c.y1 - svgBounds.minY;
                            const cx2 = c.x2 - svgBounds.minX;
                            const cy2 = c.y2 - svgBounds.minY;
                            const midX = (cx1 + cx2) / 2;
                            const path = `M ${cx1} ${cy1} C ${midX} ${cy1}, ${midX} ${cy2}, ${cx2} ${cy2}`;
                            return (
                                <g key={i}>
                                    <path className="connection-glow" d={path} />
                                    <path
                                        className="connection-line"
                                        d={path}
                                        style={c.isRec ? { opacity: 0.3 } : undefined}
                                    />
                                </g>
                            );
                        })}
                    </svg>

                    {/* Track Nodes */}
                    {nodeOrder.map((id) => {
                        const node = nodes[id];
                        if (!node) return null;
                        const track = node.track;
                        const art = getArtwork(track);
                        const preview = getPreviewUrl(track);
                        const hasAudio = !!(preview || (track.youtubeId && track.youtubeId !== '-1') || track.soundcloudUrl);
                        const isActive = id === activeNodeId;
                        const isSelected = node.selected;
                        const isRec = node.isRecommendation;

                        let className = 'track-node';
                        if (isActive) className += ' active-node';
                        else if (isSelected) className += ' selected-node';
                        if (isRec) className += ' recommendation-node';
                        if (!hasAudio) className += ' no-preview';
                        if (menuNodeId === id) className += ' menu-open';
                        if (track.fileMissing) className += ' file-missing';
                        if (v2 && playingNodeId === id && autoPlay) className += ' playing-node';

                        return (
                            <div
                                key={id}
                                className={className}
                                style={{
                                    left: node.x,
                                    top: node.y,
                                    animationDelay: `${(node.level || 0) * 0.05}s`,
                                }}
                                onMouseDown={(e) => handleNodeMouseDown(e, id)}
                                onTouchStart={(e) => handleNodeTouchStart(e, id)}
                                onContextMenu={(e) => {
                                    // Right-click == the node's "···" button: open the same
                                    // menu, with the same clip-flip logic (node bottom is a
                                    // close stand-in for the ⋯ button's rect).
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const nodeRect = e.currentTarget.getBoundingClientRect();
                                    const limit = (canvasRef.current?.getBoundingClientRect().bottom) || window.innerHeight;
                                    setMenuOpenUp((nodeRect.bottom + 260) > limit);
                                    setMenuNodeId(id);
                                    trackEvent('track_context_menu_opened', { trigger: 'right_click', from: 'set_planner' });
                                }}
                            >
                                <div className="node-artwork">
                                    {art ? (
                                        <img src={art} alt={track.title} draggable={false} />
                                    ) : (
                                        <div className="artwork-placeholder">&#9835;</div>
                                    )}
                                    {track.fileMissing && (
                                        <div className="node-missing-badge" title="Source file not found — preview unavailable">
                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="10"/>
                                                <line x1="12" y1="8" x2="12" y2="12"/>
                                                <line x1="12" y1="16" x2="12.01" y2="16"/>
                                            </svg>
                                        </div>
                                    )}
                                    {track.inLibrary && <div
                                        onMouseEnter={(e) => { const r = e.currentTarget.getBoundingClientRect(); setLibTipPos({ x: r.right + 4, y: r.top }); setLibTipNodeId(id); }}
                                        onMouseLeave={() => setLibTipNodeId(prev => prev === id ? null : prev)}
                                        style={{ position: 'absolute', top: 3, left: 3, width: 18, height: 18, borderRadius: '50%', background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 15, cursor: 'default' }}
                                    >
                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="#4cc2f1"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55C7.79 13 6 14.79 6 17s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/></svg>
                                    </div>}
                                    {/* For recommendations: + button top-right */}
                                    {isRec && (
                                        <div
                                            className="node-add-btn"
                                            onClick={(e) => { e.stopPropagation(); handleSelectRec(id); }}
                                            title="Add to set"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                        </div>
                                    )}
                                    {/* For selected nodes (not first): X button top-right to break chain */}
                                    {!isRec && isSelected && setPath.indexOf(id) > 0 && recentlySelectedId !== id && (
                                        <div
                                            className="node-remove-btn"
                                            onClick={(e) => { e.stopPropagation(); handleBreakChain(id); }}
                                            title="Remove from set"
                                        >
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                        </div>
                                    )}
                                    {/* Play/pause center button for all nodes with audio */}
                                    {hasAudio && (
                                        <div
                                            className={`node-play-center${loadingNodeId === id ? ' loading' : ''}`}
                                            onClick={(e) => handlePlayPreview(e, track, id)}
                                        >
                                            <div className="play-btn-circle">
                                                {loadingNodeId === id
                                                    ? <span className="mix-play-spinner" />
                                                    : (playingNodeId === id && autoPlay ? <PauseIcon /> : <PlayIcon className="play-glyph" />)}
                                            </div>
                                        </div>
                                    )}
                                    {/* More actions button */}
                                    <span
                                        className="node-more-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const opening = menuNodeId !== id;
                                            if (opening) {
                                                // Flip the menu up if it would clip off the bottom of the
                                                // canvas. ~260px covers the tallest menu (copy / open /
                                                // several artists / add-to / maybe-later / remove).
                                                const btn = e.currentTarget.getBoundingClientRect();
                                                const limit = (canvasRef.current?.getBoundingClientRect().bottom) || window.innerHeight;
                                                setMenuOpenUp((btn.bottom + 260) > limit);
                                                trackEvent('track_context_menu_opened', { trigger: 'kebab', from: 'set_planner' });
                                            }
                                            setMenuNodeId(opening ? id : null);
                                        }}
                                    >···</span>
                                </div>
                                {menuNodeId === id && (
                                    <div className={`node-menu${menuOpenUp ? ' menu-up' : ''}`} onClick={(e) => e.stopPropagation()}>
                                        <button onClick={() => {
                                            const label = track.artist ? `${track.artist} - ${track.title}` : track.title;
                                            navigator.clipboard.writeText(label);
                                            toast.success('Copied');
                                            setMenuNodeId(null);
                                        }}>
                                            <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                                            Copy name
                                        </button>
                                        {(track.title || track.artist) && (
                                            <button onClick={() => {
                                                const query = track.artist && track.title
                                                    ? `${track.artist} - ${track.title}`
                                                    : (track.title || track.artist);
                                                // Order matters: setActiveSearch BEFORE setActiveTab so the
                                                // nav stack's clean marker captures activeSearch='track' and
                                                // the subsequent setTrackResults replaces it (one entry total)
                                                // instead of pushing a separate stale-marker entry.
                                                dispatch(setTrackQuery(query));
                                                dispatch(setActiveSearch('track'));
                                                dispatch(setActiveTab('djpal'));
                                                dispatch(searchTrack({ trackName: query, trackId: entityId(track) || null }));
                                                setMenuNodeId(null);
                                            }}>
                                                {getArtwork(track) ? (
                                                    <img className="menu-thumb" src={getArtwork(track)} alt="" />
                                                ) : (
                                                    <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                                                )}
                                                Open in Mixpedia
                                            </button>
                                        )}
                                        {(() => {
                                            const seen = new Set();
                                            const list = [];
                                            const push = (a) => {
                                                if (!a?.name) return;
                                                const key = a.id != null ? `id:${a.id}` : `name:${a.name.toLowerCase()}`;
                                                if (seen.has(key)) return;
                                                seen.add(key);
                                                list.push(a);
                                            };
                                            (track.artists || []).forEach(push);
                                            (track.remixers || []).forEach(push);
                                            // Fallback when the rec didn't carry structured artists
                                            // (e.g. library-added tracks): naive single-artist nav.
                                            if (list.length === 0 && track.artist) {
                                                push({ name: track.artist, id: null });
                                            }
                                            return list.map((a) => (
                                                <button key={a.id || `n:${a.name}`} onClick={() => {
                                                    // setActiveSearch BEFORE setActiveTab so the clean
                                                    // marker in the nav stack matches the subsequent
                                                    // setArtistResults (one entry, one back-click).
                                                    dispatch(setArtistQuery(a.name));
                                                    dispatch(setActiveSearch('artist'));
                                                    dispatch(setActiveTab('djpal'));
                                                    dispatch(searchArtist({ artistName: a.name, artistId: a.id || null }));
                                                    setMenuNodeId(null);
                                                }}>
                                                    {a.artworkUrl ? (
                                                        <img className="menu-thumb menu-thumb-circle" src={safeArtwork(a.artworkUrl)} alt="" />
                                                    ) : (
                                                        <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <circle cx="12" cy="8" r="4"/>
                                                            <path d="M4 21v-1a8 8 0 0116 0v1"/>
                                                        </svg>
                                                    )}
                                                    Open {a.name} in Mixpedia
                                                </button>
                                            ));
                                        })()}
                                        {fullFirstQueueSrc({ ...track, filePath: filePathToAudioUrl(track.filePath), previewUrl: getPreviewUrl(track) }) && (
                                            <button onClick={() => {
                                                // Full-first like every play path (was the 30s sample only).
                                                addToNextUp(dispatch, {
                                                    name: track.title || 'Unknown Track',
                                                    singer: track.artist || '',
                                                    cover: resolveCover(getArtwork(track) || filePathToArtUrl(track.filePath)),
                                                    ...fullFirstQueueSrc({ ...track, filePath: filePathToAudioUrl(track.filePath), previewUrl: getPreviewUrl(track) }),
                                                });
                                                setMenuNodeId(null);
                                            }}>
                                                <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                                                Add to queue
                                            </button>
                                        )}
                                        <button onClick={() => { setPlaylistModalTrack(track); setMenuNodeId(null); }}>
                                            <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="5" x2="21" y2="5"/><line x1="3" y1="11" x2="21" y2="11"/><line x1="3" y1="17" x2="12" y2="17"/><line x1="18" y1="14" x2="18" y2="22"/><line x1="14" y1="18" x2="22" y2="18"/></svg>
                                            Add to Playlist
                                        </button>
                                        {isRec && (
                                            <button onClick={() => handleMaybeLater(track)}>
                                                <svg className="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                                Maybe later
                                            </button>
                                        )}
                                        {isAuthenticated && track.artist && (
                                            <button onClick={() => {
                                                const key = `${track.artist}|||${track.title}`;
                                                if (likedSet[key]) {
                                                    dispatch(unlikeTrackThunk({ artist: track.artist, title: track.title }));
                                                    toast.info('Removed from likes');
                                                } else {
                                                    dispatch(likeTrackThunk(track));
                                                    toast.success('Added to likes');
                                                }
                                                setMenuNodeId(null);
                                            }}>
                                                <svg className="menu-icon" viewBox="0 0 24 24" fill={likedSet[`${track.artist}|||${track.title}`] ? '#ff4757' : 'none'} stroke={likedSet[`${track.artist}|||${track.title}`] ? '#ff4757' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
                                                {likedSet[`${track.artist}|||${track.title}`] ? 'Unlike' : 'Like'}
                                            </button>
                                        )}
                                    </div>
                                )}
                                {/* Refresh button next to active node - always visible */}
                                {isActive && !isRec && (
                                    <div
                                        className="node-refresh-btn"
                                        onClick={(e) => { e.stopPropagation(); handleRefreshRecs(); }}
                                        title="Get different recommendations"
                                    >
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 019-9 9.75 9.75 0 016.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 01-9 9 9.75 9.75 0 01-6.74-2.74L3 16"/><path d="M8 16H3v5"/></svg>
                                    </div>
                                )}
                                <div className="node-info">
                                    <span className="node-title" title={track.title}>{track.title}</span>
                                    {track.artist && (
                                        <span className="node-artist" title={track.artist}>{track.artist}</span>
                                    )}
                                    <div className="node-meta">
                                        {track.bpm && <span className="meta-pill">{Math.round(track.bpm)} BPM</span>}
                                        {track.key && (
                                            camelotColor(track.key)
                                                ? <span className="meta-pill" style={{ background: camelotColor(track.key), color: '#1a1a1a', borderColor: 'transparent', fontWeight: 600, minWidth: '30px', textAlign: 'center' }}>{formatKeyDisplay(track.key, keyNotation)}</span>
                                                : <span className="meta-pill">{formatKeyDisplay(track.key, keyNotation)}</span>
                                        )}
                                    </div>
                                    {track.score != null && (
                                        <span className="node-score">{Math.round(track.score * 100)}%</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Loading */}
                {isLoading && (
                    <div className="whats-next-loading">
                        <div className="loading-spinner" />
                        <span className="loading-text">Finding what's next...</span>
                    </div>
                )}

                {/* Error */}
                {lockedPreview && !isLoading && (
                    <div className="free-steps-wall" data-more={lockedPreview.moreCount}>
                        <div className="free-steps-wall-rows" aria-hidden="true">
                            {[0, 1, 2].map((i) => (
                                <div key={i} className="free-steps-wall-row"><span className="fsw-art" /><span className="fsw-title" /><span className="fsw-artist" /></div>
                            ))}
                        </div>
                        <div className="free-steps-wall-text">
                            <strong>
                                {lockedPreview.moreCount > 0
                                    ? `GOAT found ${lockedPreview.moreCount} more track${lockedPreview.moreCount === 1 ? '' : 's'} for this set`
                                    : 'Your free steps for this set are used'}
                            </strong>
                            <span>Free plan: {FREE_STEP_LIMIT} recommendation steps per set. You can still add tracks by hand.</span>
                        </div>
                        <button type="button" className="free-wall-btn" onClick={() => requireUpgrade({ moreCount: lockedPreview.moreCount })}>Unlock with <GoatBadge /></button>
                    </div>
                )}
                {error && !isLoading && (
                    <div className="rec-choice-hint" style={{ color: 'var(--color-danger)' }}>
                        {error}
                    </div>
                )}




                {/* Maybe Later panel */}
                {maybeLater.length > 0 && (
                    <div className={`maybe-later-panel ${maybeLaterOpen ? 'open' : ''}`}>
                        <button
                            className="maybe-later-toggle"
                            onClick={() => setMaybeLaterOpen(!maybeLaterOpen)}
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                            Maybe Later ({maybeLater.length})
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ transform: maybeLaterOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
                        </button>
                        {maybeLaterOpen && (
                            <div className="maybe-later-list">
                                {maybeLater.map((t, i) => (
                                    <div key={i} className="maybe-later-item" onClick={() => handleUseMaybeLater(t, i)}>
                                        <div className="maybe-later-art">
                                            {getArtwork(t) ? (
                                                <img src={getArtwork(t)} alt={t.title} />
                                            ) : (
                                                <span>&#9835;</span>
                                            )}
                                        </div>
                                        <div className="maybe-later-info">
                                            <span className="maybe-later-title">{t.title}</span>
                                            {t.artist && <span className="maybe-later-artist">{t.artist}</span>}
                                        </div>
                                        <button
                                            className="maybe-later-use"
                                            onClick={(e) => { e.stopPropagation(); handleUseMaybeLater(t, i); }}
                                            title="Add to set"
                                        >&#10003;</button>
                                        <button
                                            className="maybe-later-remove"
                                            onClick={(e) => { e.stopPropagation(); setMaybeLater(prev => prev.filter((_, j) => j !== i)); }}
                                            title="Remove"
                                        >&#10005;</button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Zoom controls */}
                <div className="canvas-zoom-controls">
                    <button onClick={() => dispatch(setZoom(Math.min(2, zoom + 0.15)))}>+</button>
                    <button onClick={() => dispatch(setZoom(Math.max(0.3, zoom - 0.15)))}>-</button>
                    <button
                        className="fit-btn"
                        onClick={() => {
                            const active = activeNodeId ? nodes[activeNodeId] : null;
                            if (!active) { dispatch(setZoom(1)); dispatch(setPan({ x: 0, y: 0 })); return; }
                            const recs = nodeOrder
                                .map(nid => nodes[nid])
                                .filter(n => n && n.parentId === activeNodeId && n.isRecommendation);
                            const fit = computeBranchFit(active, recs);
                            if (fit) {
                                dispatch(setZoom(fit.zoom));
                                dispatch(setPan({ x: fit.panX, y: fit.panY }));
                            }
                        }}
                        title="Fit active track + recs to screen"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>
                    </button>
                    <button onClick={() => { dispatch(setZoom(1)); dispatch(setPan({ x: 0, y: 0 })); }} title="Reset view">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7"/><polyline points="3 3 3 9 9 9"/></svg>
                    </button>
                </div>
            </div>

            {/* Save/Edit Details Dialog (shared JSX) */}
            {saveDialogModal}

            {/* Phone add-track sheet: two tabs, shared bodies (see renderLibraryAddBody). */}
            {isMobileWeb && showAddTrack && (
                <div className="add-track-sheet-overlay" onClick={closeAddSheet}>
                    <div className="add-track-sheet" onClick={(e) => e.stopPropagation()}>
                        <div className="add-track-sheet-grabber" aria-hidden="true" />
                        <div className="add-track-sheet-header">
                            <div className="add-track-sheet-tabs" role="tablist">
                                <button role="tab" aria-selected={addSheetTab === 'mixpedia'} className={`add-track-sheet-tab${addSheetTab === 'mixpedia' ? ' active' : ''}`} onClick={() => switchAddSheetTab('mixpedia')}>Mixpedia</button>
                                <button role="tab" aria-selected={addSheetTab === 'playlists'} className={`add-track-sheet-tab${addSheetTab === 'playlists' ? ' active' : ''}`} onClick={() => switchAddSheetTab('playlists')}>Your playlists</button>
                            </div>
                            <button className="add-track-sheet-close" onClick={closeAddSheet} aria-label="Close">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                        </div>
                        <div className="add-track-sheet-body">
                            {addSheetTab === 'mixpedia' ? renderMixpediaAddBody() : renderLibraryAddBody()}
                        </div>
                    </div>
                </div>
            )}

            {/* Saved Sets Modal (shared JSX) */}
            {savedSetsModal}

            {/* Export Modal */}
            {showExport && (
                <div className="export-playlist-modal" onClick={() => setShowExport(false)}>
                    <div className="export-modal-content export-set-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="export-modal-header">
                            <h3>Your Set ({setPath.length} tracks · <SetLength seconds={estimateSetSeconds(setPath.map(id => nodes[id]?.track).filter(Boolean))} />)</h3>
                            <button type="button" className="export-modal-close" onClick={() => setShowExport(false)} aria-label="Close">×</button>
                        </div>
                        <div className="save-dialog-input">
                            <label className="save-dialog-label" htmlFor="export-playlist-name">Playlist name</label>
                            <input
                                id="export-playlist-name"
                                type="text"
                                placeholder="Playlist name..."
                                value={exportName}
                                onChange={(e) => setExportName(e.target.value)}
                                // Desktop only: on phones auto-focus summons the
                                // keyboard the instant the sheet opens, which
                                // shoves the sheet's header + name field off the
                                // top of the screen. Users tap the field to rename.
                                autoFocus={!checkIsMobileWeb()}
                            />
                        </div>
                        <div className="save-dialog-input export-dj-name">
                            <label className="save-dialog-label" htmlFor="export-dj-name">DJ name <span className="save-dialog-label-hint">(shown on the PNG)</span></label>
                            <input
                                id="export-dj-name"
                                type="text"
                                placeholder="Your DJ / artist name"
                                value={exportDjName}
                                onChange={(e) => handleDjNameChange(e.target.value)}
                                aria-label="DJ name shown on the exported image"
                            />
                        </div>
                        <div className="export-tracks-list">
                            {setPath.map((id, i) => {
                                const track = nodes[id]?.track;
                                if (!track) return null;
                                return (
                                    <div key={id} className="export-track-item">
                                        <span className="export-track-num">{i + 1}</span>
                                        <div className="export-track-art">
                                            {getArtwork(track) ? (
                                                <img src={getArtwork(track)} alt={track.title} />
                                            ) : (
                                                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-tertiary)', fontSize: 14 }}>&#9835;</div>
                                            )}
                                        </div>
                                        <span className="export-track-name">
                                            {track.artist ? `${track.artist} - ${track.title}` : track.title}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="export-actions">
                            <button className="toolbar-btn" onClick={handleCopyPlaylist} title="Copy the numbered tracklist as plain text">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                                {copied ? 'Copied!' : 'Copy as Text'}
                            </button>
                            <button className="toolbar-btn" onClick={handleExportImage} disabled={exportingImage} title="Export set details + tracklist as a PNG image">
                                {exportingImage
                                    ? <span className="mix-play-spinner" />
                                    : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>}
                                {exportingImage ? 'Exporting…' : 'Export as PNG'}
                            </button>
                            {/* Copy · PNG · Save as Playlist are three peer exports — none is
                                "the" action, so none gets the accent. */}
                            <button className="toolbar-btn" onClick={handleExportToPlaylist} title="Create a playlist in your Library from this set">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                                Save as Playlist
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm action modal */}
            {confirmAction && (
                <div className="export-playlist-modal" onClick={() => { setConfirmAction(null); confirmAction.onCancel?.(); }}>
                    <div className="export-modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>{confirmAction.title || 'Are you sure?'}</h3>
                        <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, margin: '0 0 20px' }}>
                            {confirmAction.message}
                        </p>
                        <div className="export-actions">
                            <button onClick={() => { setConfirmAction(null); confirmAction.onCancel?.(); }}>Cancel</button>
                            {/* With a save option, Save is the primary action; otherwise Continue is. */}
                            <button className={confirmAction.onSave ? '' : 'export-copy-btn'} onClick={confirmAction.onConfirm}>
                                {confirmAction.confirmLabel || 'Continue'}
                            </button>
                            {confirmAction.onSave && (
                                <button className="export-copy-btn" onClick={confirmAction.onSave}>{confirmAction.saveLabel}</button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
        {libTipNodeId && <div style={{ position: 'fixed', left: libTipPos.x, top: libTipPos.y, background: 'rgba(0,0,0,0.9)', color: '#4cc2f1', fontSize: 10, fontWeight: 600, padding: '4px 8px', borderRadius: 4, whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 9999 }}>In your library</div>}
    </>);
};

export default WhatsNextTab;
